// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the shared contract for Agents: coding agents (T3 Code now; Codex and Claude Code later)
// on a person's own machines, reached through the konvo-agents bridge. The bridge only connects
// out to Konvo (long-poll), Konvo keeps no copies of thread content, and each machine's owner sets
// what Konvo may do on that machine. See docs/konvo-agents/DESIGN.md. The bridge package
// (konvo_agents/src/adapters/types.ts) mirrors the bridge-facing shapes; keep them in step.

import {z} from 'zod';
import {
	AgentBrowserLimitSchema,
	AgentBrowserTunnelArgsSchema,
	AgentBrowserTunnelDataSchema,
	AgentDevServersArgsSchema,
	AgentDevServersSchema,
} from './AgentBrowser';
import {AgentMachineStatsArgsSchema, AgentMachineStatsSchema} from './AgentMachineStats';
import {
	AgentLegacyTerminalEventSchema,
	AgentTerminalTunnelArgsSchema,
	AgentTerminalTunnelDataSchema,
} from './AgentTerminals';
import {plainAgentTitle} from './AgentTitle';

// ---------------------------------------------------------------------------
// What the bridge reports
// ---------------------------------------------------------------------------

export const AGENT_TOOL_IDS = ['t3'] as const;
export type AgentToolId = (typeof AGENT_TOOL_IDS)[number];

/** One choice of a model option as the agent tool shows it, e.g. {id: 'priority', label: 'Fast', description: '2x speed'}. */
export const AgentModelOptionSchema = z.object({
	id: z.string().max(40),
	label: z.string().max(40),
	description: z.string().max(120).optional(),
});
export type AgentModelOption = z.infer<typeof AgentModelOptionSchema>;

/** A model the agent tool can run, as its model picker lists it (konvo-agents 0.9.0+). */
export const AgentModelSchema = z.object({
	/** The tool's provider instance, e.g. "codex" or "claudeAgent". */
	provider: z.string().max(80),
	/** The provider as people know it, e.g. "Codex" or "Claude". */
	providerName: z.string().max(80),
	/** The model id the tool takes, e.g. "gpt-6.1-sol". */
	model: z.string().max(120),
	/** The model's display name, e.g. "GPT-6.1-Sol". */
	name: z.string().max(120),
	/** Other names it answers to, e.g. "opus". */
	aliases: z.array(z.string().max(80)).max(10).optional(),
	/** The provider's default model. */
	isDefault: z.boolean().optional(),
	/** An older model the tool still offers. */
	legacy: z.boolean().optional(),
	/** Reasoning levels it takes, lowest first, and the one it uses unless told otherwise. */
	efforts: z.array(z.string().max(40)).max(10).optional(),
	defaultEffort: z.string().max(40).optional(),
	/** The tool's words for those levels, e.g. "Extra High" for xhigh (konvo-agents 0.13.0+). */
	effortOptions: z.array(AgentModelOptionSchema).max(10).optional(),
	/** Service tiers, e.g. Standard and Fast, when the model has them (konvo-agents 0.13.0+). */
	serviceTiers: z.array(AgentModelOptionSchema).max(10).optional(),
	defaultServiceTier: z.string().max(40).optional(),
	/** The tool's badge for it, e.g. "new" (konvo-agents 0.13.0+). */
	badge: z.string().max(20).optional(),
});
export type AgentModel = z.infer<typeof AgentModelSchema>;

/** Which model a thread runs on, how hard it thinks and how fast it is served. */
export const AgentModelChoiceSchema = z.object({
	provider: z.string().max(80),
	model: z.string().max(120),
	effort: z.string().max(40).optional(),
	/** One of the model's serviceTiers, e.g. priority for Fast (konvo-agents 0.13.0+; older bridges ignore it). */
	serviceTier: z.string().max(40).optional(),
});
export type AgentModelChoice = z.infer<typeof AgentModelChoiceSchema>;

/** One usage window of a plan, e.g. Claude's 5-hour session or a weekly limit (konvo-agents 0.10.0+). */
export const AgentUsageWindowSchema = z.object({
	/** The provider's id for it, e.g. five_hour, seven_day, primary. */
	id: z.string().max(80),
	/** session, weekly, ... */
	kind: z.string().max(40),
	/** As the agent tool shows it, e.g. "Session" or "Weekly · Fable". */
	label: z.string().max(80),
	/** 0-100. */
	usedPercent: z.number().min(0).max(100),
	resetsAt: z.string().max(40).optional(),
	windowMinutes: z.number().int().positive().max(100_000).optional(),
});
export type AgentUsageWindow = z.infer<typeof AgentUsageWindowSchema>;

/** Commands are discovered for an existing thread or a project's selected provider. */
export const AgentCommandsRequestSchema = z.union([
	z.object({threadId: z.string().min(1).max(200)}).strict(),
	z.object({projectId: z.string().min(1).max(200), provider: z.string().min(1).max(80)}).strict(),
]);
export type AgentCommandsRequest = z.infer<typeof AgentCommandsRequestSchema>;
export const AgentSlashCommandSchema = z.object({
	name: z.string().regex(/^[a-zA-Z0-9][a-zA-Z0-9_.:-]{0,79}$/),
	description: z.string().max(300).optional(),
	argumentHint: z.string().max(120).optional(),
	project: z.boolean().optional(),
});
export type AgentSlashCommand = z.infer<typeof AgentSlashCommandSchema>;
export const AgentCommandsSchema = z.object({
	provider: z.string().max(80),
	providerName: z.string().max(80),
	commands: z.array(AgentSlashCommandSchema).max(200),
	pending: z.boolean().optional(),
});
export type AgentCommands = z.infer<typeof AgentCommandsSchema>;

export const AgentResetCreditsSchema = z.object({
	availableCount: z.number().int().min(0).max(1_000_000),
	nextExpiresAt: z.string().datetime().optional(),
});
export const AgentUsageResetRequestSchema = z.object({
	provider: z.string().min(1).max(80),
	account: z.string().min(1).max(60),
	availableCount: z.number().int().min(1).max(1_000_000),
});
export type AgentUsageResetRequest = z.infer<typeof AgentUsageResetRequestSchema>;
export const AgentUsageResetResultSchema = z.object({
	outcome: z.enum(['reset', 'nothingToReset', 'noCredit', 'alreadyRedeemed']),
});
export type AgentUsageResetResult = z.infer<typeof AgentUsageResetResultSchema>;

/** A signed-in provider's plan and how much of it is used, as the agent tool last checked (konvo-agents 0.10.0+). */
export const AgentPlanUsageSchema = z.object({
	/** The tool's provider instance, e.g. "codex" or "claudeAgent". */
	provider: z.string().max(80),
	providerName: z.string().max(80),
	/** e.g. "Claude Max" or "ChatGPT Business". */
	plan: z.string().max(80).optional(),
	/** A hash of the provider and the login (never the login), so one account on two machines counts once. */
	account: z.string().max(60).optional(),
	checkedAt: z.string().max(40),
	windows: z.array(AgentUsageWindowSchema).max(10),
	resetCredits: AgentResetCreditsSchema.optional(),
});
export type AgentPlanUsage = z.infer<typeof AgentPlanUsageSchema>;

export const AgentSetupRequestSchema = z
	.object({
		provider: z.enum(['claudeAgent', 'codex']),
		/** Recheck this configured instance without changing its executable, environment or login. */
		instanceId: z.string().min(1).max(80).optional(),
		/** An existing CLI path supplied by the owner; never a shell command. */
		binaryPath: z
			.string()
			.trim()
			.min(1)
			.max(2000)
			.refine((path) => ![...path].some((char) => char.charCodeAt(0) < 32))
			.optional(),
	})
	.refine((request) => !request.instanceId || !request.binaryPath, 'A connection recheck cannot change the CLI path.');
export type AgentSetupRequest = z.infer<typeof AgentSetupRequestSchema>;

export const AGENT_UPDATE_STATUSES = ['idle', 'queued', 'running', 'succeeded', 'failed', 'unchanged'] as const;
export type AgentUpdateStatus = (typeof AGENT_UPDATE_STATUSES)[number];

/**
 * Whether a newer Claude Code or Codex is out, and how its update from Konvo went, as the agent
 * tool reports it (T3 Code's versionAdvisory and updateState; konvo-agents 0.40.0+).
 */
export const AgentUpdateInfoSchema = z.object({
	/** A newer version than the installed one is out. */
	available: z.boolean(),
	/** The tool can update it itself (the agentUpdate op); otherwise `command` is run by hand. */
	canUpdate: z.boolean(),
	/** The command that updates it on the machine, e.g. "claude update" (shown to copy when it can't). */
	command: z.string().max(300).optional(),
	/** The latest update's progress: idle until one is asked for; succeeded / failed / unchanged once over. */
	status: z.enum(AGENT_UPDATE_STATUSES),
	/** The tool's words about the update, e.g. why it failed. */
	message: z.string().max(400).optional(),
	startedAt: z.string().max(40).optional(),
	finishedAt: z.string().max(40).optional(),
});
export type AgentUpdateInfo = z.infer<typeof AgentUpdateInfoSchema>;

export const AgentReadinessSchema = z.object({
	provider: z.enum(['claudeAgent', 'codex']),
	status: z.enum(['ready', 'sign_in', 'not_installed', 'checking', 'disabled', 'unavailable']),
	/** The installed version, e.g. "2.1.293" (konvo-agents 0.40.0+). */
	version: z.string().max(40).optional(),
	/** The newest published version, when the tool knows it (konvo-agents 0.40.0+). */
	latestVersion: z.string().max(40).optional(),
	update: AgentUpdateInfoSchema.optional(),
});
export type AgentReadiness = z.infer<typeof AgentReadinessSchema>;

/** Configured accounts remain visible even when their models cannot currently run. */
export const AgentConnectionSchema = z.object({
	provider: z.string().min(1).max(80),
	providerName: z.string().max(80),
	driver: z.string().max(80),
	status: AgentReadinessSchema.shape.status,
	checkedAt: z.string().max(40),
	models: z.array(AgentModelSchema).max(200),
	usageAvailable: z.boolean(),
	/** Safe metadata only; never credentials or arbitrary provider error output. */
	loginExpiresAt: z.string().max(40).optional(),
	checkCommand: z.string().max(5000).optional(),
	loginCommand: z.string().max(5000).optional(),
	/**
	 * The account's T3 settings, for editing it from Konvo (konvo-agents 0.51.0+, `can.accounts`).
	 * Environment variables come as names only: their values never leave the machine.
	 */
	config: z
		.object({
			displayName: z.string().max(80).optional(),
			accentColor: z.string().max(20).optional(),
			enabled: z.boolean(),
			/** The sign-in folder (CLAUDE_CONFIG_DIR / CODEX_HOME); empty = the agent's default. */
			homePath: z.string().max(2000).optional(),
			binaryPath: z.string().max(2000).optional(),
			environmentNames: z.array(z.string().max(120)).max(50),
		})
		.optional(),
});
export type AgentConnection = z.infer<typeof AgentConnectionSchema>;

/** The agents an account can be for (T3 provider drivers Konvo can add). */
export const AGENT_ACCOUNT_DRIVERS = ['claudeAgent', 'codex'] as const;
export type AgentAccountDriver = (typeof AGENT_ACCOUNT_DRIVERS)[number];
const accountText = (max: number) =>
	z
		.string()
		.trim()
		.max(max)
		.refine((value) => ![...value].some((char) => char.charCodeAt(0) < 32), 'No control characters.');

/**
 * POST /konvo/agents/machines/:id/accounts (owner only): adds, edits, turns off or removes a Claude
 * or Codex account (a T3 provider instance) on the machine. The bridge answers with the tool info.
 */
export const AgentAccountRequestSchema = z.discriminatedUnion('action', [
	z.object({
		action: z.literal('save'),
		/** Left out: a new account (the bridge picks the id from the name). */
		instanceId: z.string().min(1).max(80).optional(),
		driver: z.enum(AGENT_ACCOUNT_DRIVERS),
		displayName: accountText(40).pipe(z.string().min(1)),
		accentColor: z
			.string()
			.regex(/^#[0-9a-fA-F]{6}$/)
			.optional(),
		enabled: z.boolean().optional(),
		/** Left out on a new account: the bridge makes ~/.claude-<name> or ~/.codex-<name>. */
		homePath: accountText(2000).optional(),
		/** An existing CLI path; left out: the one T3 already finds. Never a shell command. */
		binaryPath: accountText(2000).optional(),
		/** Variables to set (a value) or drop (null); others stay as they are. */
		environment: z
			.array(
				z.object({
					name: z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,119}$/),
					value: accountText(4000).nullable(),
				}),
			)
			.max(50)
			.optional(),
	}),
	z.object({action: z.literal('remove'), instanceId: z.string().min(1).max(80)}),
]);
export type AgentAccountRequest = z.infer<typeof AgentAccountRequestSchema>;

/** POST /konvo/agents/machines/:id/agent-update: updates Claude Code or Codex on the owner's machine. */
export const AgentUpdateRequestSchema = z.object({provider: z.enum(['claudeAgent', 'codex'])});
export type AgentUpdateRequest = z.infer<typeof AgentUpdateRequestSchema>;

/** POST /konvo/agents/machines/:id/device-access: lets new chats on the owner's machine use simulators, or stops it. */
export const AgentDeviceAccessRequestSchema = z.object({enabled: z.boolean()});
export type AgentDeviceAccessRequest = z.infer<typeof AgentDeviceAccessRequestSchema>;

export const AgentToolInfoSchema = z.object({
	agents: z.array(AgentReadinessSchema).max(2).optional(),
	tool: z.enum(AGENT_TOOL_IDS),
	name: z.string().max(80),
	version: z.string().max(40),
	/** The tool's own Agent device access setting (konvo-agents 0.68.0+): new chats can use simulators on the machine. */
	deviceAccess: z.boolean().optional(),
	can: z.object({
		/** Reports and changes Agent device access (the deviceAccess op; konvo-agents 0.68.0+). */
		deviceAccess: z.boolean().optional(),
		/** Per-instance connection rechecks (konvo-agents 0.50.0+). */
		connections: z.boolean().optional(),
		/** Adds, edits, turns off and removes Claude and Codex accounts (the account op; konvo-agents 0.51.0+). */
		accounts: z.boolean().optional(),
		/** Owner-requested local agent discovery and existing-path recovery (0.29.0+). */
		setup: z.boolean().optional(),
		resetUsage: z.boolean().optional(),
		commands: z.boolean().optional(),
		send: z.boolean(),
		start: z.boolean(),
		interrupt: z.boolean(),
		approve: z.boolean(),
		/** Can move threads in and out of the tool's Settled section (konvo-agents 0.6.0+). */
		settle: z.boolean().optional(),
		/** Renames threads (konvo-agents 0.24.0+). */
		rename: z.boolean().optional(),
		/** Archives and unarchives threads, and lists the archived ones (konvo-agents 0.36.0+). */
		archive: z.boolean().optional(),
		/** Deletes threads from the agent tool for good (konvo-agents 0.36.0+). */
		delete: z.boolean().optional(),
		/** Takes a model (and reasoning level) on start and send (konvo-agents 0.9.0+; older bridges ignore one). */
		model: z.boolean().optional(),
		/** Takes pictures and files with the first prompt of a new thread (konvo-agents 0.9.0+). */
		startFiles: z.boolean().optional(),
		/** Takes attachments with answers to questions (konvo-agents 0.49.0+). */
		questionFiles: z.boolean().optional(),
		/** Lists a project's local branches for a new thread to start from (konvo-agents 0.11.0+). */
		branches: z.boolean().optional(),
		/** Fetches pictures and files from Konvo by upload id, so they can be up to 50 MB (konvo-agents 0.19.0+). */
		uploadLinks: z.boolean().optional(),
		/** Adds, renames and removes projects, and lists the machine's folders to add one from (konvo-agents 0.22.0+). */
		projects: z.boolean().optional(),
		/** Shows what a turn or the whole thread changed, as a diff (konvo-agents 0.25.0+). */
		diff: z.boolean().optional(),
		/** Searches what was said in the tool's threads (konvo-agents 0.25.0+). */
		search: z.boolean().optional(),
		/** A new chat in the project's checkout can switch it to another branch first (konvo-agents 0.25.0+). */
		switchBranch: z.boolean().optional(),
		/**
		 * Says why a switch fails (SWITCH_FAILED with switchProblem), checks one ahead (branches with
		 * switchTo) and can put the changes aside first (start with stash; konvo-agents 0.41.0+).
		 */
		checkoutHelp: z.boolean().optional(),
		/** Opens the files a reply names and browses the thread's project (fileRefs, fileList, fileSearch, fileText, file; konvo-agents 0.26.0+). */
		files: z.boolean().optional(),
		/** Sends small versions of pictures, made on the machine (the `picture` arg of file and media; konvo-agents 0.69.0+). */
		pictureSizes: z.boolean().optional(),
		/** Closes an agent's question without answering it (the dismiss op; konvo-agents 0.36.0+). */
		dismissQuestion: z.boolean().optional(),
		/** Answers the agent tool's requests for a secret (the secret op; konvo-agents 0.53.0+, T3 protocol 2). */
		secretRequests: z.boolean().optional(),
		/** Starts chats that run in Claude's cloud (start with cloud) and pushes a branch first (push; konvo-agents 0.54.0+). */
		cloud: z.boolean().optional(),
		/** Hands a chat over to the cloud with its work in progress (the cloudHandoff op; konvo-agents 0.55.0+). */
		cloudHandoff: z.boolean().optional(),
		/** Makes a project for a chat in a folder of its own (the toProject op; konvo-agents 0.61.0+). */
		chatToProject: z.boolean().optional(),
		/** Carries the Agents browser's tunnel and lists running dev servers (browserTunnel, devServers; AgentBrowser.ts). */
		browser: z.boolean().optional(),
		/** Opens terminals and types into them live (op terminalTunnel; AgentTerminals.ts terminal sessions). */
		terminalSessions: z.boolean().optional(),
		/** Updates Claude Code and Codex on the machine (the agentUpdate op; konvo-agents 0.40.0+). */
		updateAgents: z.boolean().optional(),
		/** Compacts a chat's context on request (the compact op; konvo-agents 0.47.0+, T3 protocol 1). */
		compact: z.boolean().optional(),
		/** Restarts a chat's agent process so new skills and MCP servers load (the restartSession op; konvo-agents 0.47.0+). */
		restartSession: z.boolean().optional(),
		/** Rewinds a chat to before one of its prompts, files too in a worktree (the rewind op; konvo-agents 0.47.0+). */
		rewind: z.boolean().optional(),
		/** Takes other chats and pull requests mentioned in a prompt (send/start `mentions`; konvo-agents 0.57.0+). */
		mentions: z.boolean().optional(),
		/** Takes `[label](attached:<name>)` links to the prompt's own pictures and files where it refers to them (konvo-agents 0.59.0+). */
		attachedLinks: z.boolean().optional(),
		/** Snoozes a chat in the agent tool too, so it sleeps there as well (the snooze op; konvo-agents 0.57.0+). */
		snooze: z.boolean().optional(),
		/** Stable send/start command and message identities (konvo-agents 0.64.0+). */
		sendIdentity: z.boolean().optional(),
		/** Queues a prompt behind the running turn (send `queue`), lists and edits the queue (the queued op; konvo-agents 0.58.0+). */
		queue: z.boolean().optional(),
		/** Stops a dev server a chat's terminal started (the stopDevServer op; konvo-agents 0.58.0+). */
		stopDevServer: z.boolean().optional(),
		/** Stop (interrupt) also ends work left running after the turn: background commands, subagents (konvo-agents 0.58.0+). */
		stopBackground: z.boolean().optional(),
	}),
	/** Set when the machine is signed in to GitHub (`gh`), so it can clone repositories as new projects (konvo-agents 0.22.0+). */
	github: z.object({account: z.string().max(100).optional()}).optional(),
	/** The models the tool can run now: installed, switched on and signed in (konvo-agents 0.9.0+). */
	models: z.array(AgentModelSchema).max(200).optional(),
	connections: z.array(AgentConnectionSchema).max(40).optional(),
	/** What a new thread runs on when neither the project nor Konvo picks a model. */
	defaultModel: AgentModelChoiceSchema.optional(),
	/** Plan usage of each signed-in provider that reports it (konvo-agents 0.10.0+). */
	usage: z.array(AgentPlanUsageSchema).max(10).optional(),
});
export type AgentToolInfo = z.infer<typeof AgentToolInfoSchema>;

/** Set on the machine (konvo-agents config); Konvo shows them read-only and enforces them too. */
export const AgentLimitsSchema = z.object({
	/** 'all', or the project IDs the owner shares. */
	projects: z.union([z.literal('all'), z.array(z.string().max(200)).max(500)]),
	/** Send prompts, start threads, stop and settle them. */
	send: z.boolean(),
	/** Answer an agent's approval requests (runs commands on that machine). Separate opt-in. */
	approve: z.boolean(),
	/**
	 * Team sharing: how far the owner may share single threads with teammates in Konvo (konvo-agents
	 * 0.25.0+). Left out (older bridges) or 'off': no sharing. Konvo can share less, never more.
	 */
	share: z.enum(['off', 'watch', 'comment', 'prompt']).optional(),
	/** What the Agents browser may reach through the machine; Konvo's setting (AgentBrowser.ts). */
	browser: AgentBrowserLimitSchema.optional(),
	/** The machine owner's own cap on it (`konvo-agents config`): the narrower of the two applies. */
	browserCap: z.enum(['off', 'machine', 'lan', 'all']).optional(),
	/** Terminals from Konvo: Konvo's setting (on unless the owner turned it off). */
	terminals: z.boolean().optional(),
	/** The machine owner's own cap (`konvo-agents config --terminals off`): off wins over Konvo's setting. */
	terminalsCap: z.enum(['off', 'on']).optional(),
});
export type AgentLimits = z.infer<typeof AgentLimitsSchema>;

/** Longest project favicon the bridge sends, as a data URL (konvo-agents FAVICON_DATA_URL_MAX). */
export const AGENT_PROJECT_FAVICON_MAX = 32_768;

export const AgentProjectSchema = z.object({
	id: z.string().max(200),
	title: z.string().max(200),
	path: z.string().max(1000).optional(),
	/** The model a new thread in this project runs on by default (konvo-agents 0.9.0+). */
	model: AgentModelChoiceSchema.optional(),
	/** Archived in T3 (0.1.6+): its chats keep it, but new chats don't offer it (konvo-agents 0.60.0+). */
	archived: z.boolean().optional(),
	/** T3's No project: each chat there works in a scratch folder of its own (konvo-agents 0.61.0+). */
	scratch: z.boolean().optional(),
	/**
	 * The project's favicon, found in its folder the way T3 Code finds it, as a small data URL
	 * (konvo-agents 0.63.0+). Its icon in the Agents list unless the person picked one. One that does
	 * not fit is dropped rather than failing the machine's whole report.
	 */
	favicon: z
		.string()
		.max(AGENT_PROJECT_FAVICON_MAX)
		.regex(/^data:image\/(png|jpeg|gif|webp|avif|svg\+xml|x-icon);base64,[A-Za-z0-9+/]+=*$/)
		.optional()
		.catch(undefined),
});
export type AgentProject = z.infer<typeof AgentProjectSchema>;

/**
 * Where a thread started from when it was sent to an agent from Konvo: the person and the message,
 * so the finished thread can offer to reply to them there (Konvo keeps it, the machine never sees it).
 */
export const AgentThreadSourceSchema = z.discriminatedUnion('kind', [
	z.object({
		kind: z.literal('message'),
		channelId: z.string().max(40),
		guildId: z.string().max(40).nullable(),
		messageId: z.string().max(40),
		name: z.string().max(200),
		text: z.string().max(4000),
		/** When it was sent, for its card in the chat. */
		at: z.string().max(40).optional(),
	}),
	z.object({
		kind: z.literal('whatsapp'),
		jid: z.string().max(200),
		messageId: z.string().max(200),
		name: z.string().max(200),
		text: z.string().max(4000),
		/** When it was sent, for its card in the chat. */
		at: z.string().max(40).optional(),
	}),
	z.object({
		kind: z.literal('mail'),
		messageId: z.string().max(600),
		name: z.string().max(200),
		subject: z.string().max(500),
		text: z.string().max(4000),
		/** When it was sent, for its card in the chat. */
		at: z.string().max(40).optional(),
	}),
	/** Sent from a ClickUp task: the chat's agent time belongs to it (no Reply to …). */
	z.object({
		kind: z.literal('task'),
		taskId: z
			.string()
			.max(60)
			.regex(/^[A-Za-z0-9_-]+$/),
		name: z.string().max(500),
		text: z.string().max(4000),
	}),
	/** Continued from another agent chat on the same machine (Continue in the cloud; no Reply to …). */
	z.object({
		kind: z.literal('agent'),
		threadId: z.string().max(200),
		name: z.string().max(500),
		text: z.string().max(4000),
	}),
]);
export type AgentThreadSource = z.infer<typeof AgentThreadSourceSchema>;

/** Where a chat went on after Continue in the cloud: the cloud chat, its branch and when (kept a month). */
export const AGENT_FOLDER_PATH_MAX_LENGTH = 1000;

export const AgentContinuedInSchema = z.object({
	/** Where the chat went on: in the cloud (left out: older records), in a new chat in a project, or itself, moved into one. */
	kind: z.enum(['cloud', 'project', 'moved']).optional(),
	/** The chat it went on in (the chat itself when it moved). */
	threadId: z.string().max(200),
	/** The cloud chat's hand-off branch. */
	branch: z.string().max(300).optional(),
	/** The project's name and folder (project, moved). */
	project: z.string().max(200).optional(),
	path: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH).optional(),
	at: z.string().max(40),
});
export type AgentContinuedIn = z.infer<typeof AgentContinuedInSchema>;

export const AgentToProjectArgsSchema = z.object({
	threadId: z.string().max(200),
	title: z.string().trim().min(1).max(200),
	/** On the machine: absolute, or ~/… for its home folder. */
	path: z.string().trim().min(1).max(AGENT_FOLDER_PATH_MAX_LENGTH),
	move: z.boolean(),
});
export type AgentToProjectArgs = z.infer<typeof AgentToProjectArgsSchema>;

export const AgentToProjectResultSchema = z.object({
	projectId: z.string().max(200),
	title: z.string().max(200),
	path: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH),
	/** The chat itself moved into the project (a No project chat). */
	moved: z.boolean(),
	/** An empty project the agent had made at the folder, replaced by the moved chat's. */
	replaced: z.string().max(200).optional(),
});
export type AgentToProjectResult = z.infer<typeof AgentToProjectResultSchema>;

/** A chat's folder against GitHub, and with push its work in progress on a hand-off branch (konvo-agents 0.55.0+). */
export const AgentCloudHandoffSchema = z.object({
	branch: z.string().max(300),
	changedFiles: z.number().int().min(0),
	unpushedCommits: z.number().int().min(0),
	pushed: z.boolean(),
});
export type AgentCloudHandoff = z.infer<typeof AgentCloudHandoffSchema>;

/** What Continue in the cloud shows before it starts: the summary the cloud chat begins with and what goes to GitHub. */
export const AgentCloudHandoffPreviewSchema = AgentCloudHandoffSchema.extend({
	title: z.string().max(500),
	/** The chat's first request, its latest and its last reply (or the one message sent), as far as Konvo reads it. */
	started: z.string().max(4000).nullable(),
	latest: z.string().max(4000).nullable(),
	reply: z.string().max(4000).nullable(),
	/** The Claude model the cloud chat runs on: the chat's own when it is Claude, else the machine's. */
	model: AgentModelChoiceSchema.nullable(),
});
export type AgentCloudHandoffPreview = z.infer<typeof AgentCloudHandoffPreviewSchema>;

/** A project's local branches for a new thread to start from, newest commit first. */
export const AGENT_SWITCH_REASONS = ['changes', 'openElsewhere', 'missing', 'busy', 'unknown'] as const;

/**
 * Why a project's checkout can't switch to another branch (konvo-agents 0.41.0+): uncommitted
 * changes in the way (named, never their contents), the branch open in another worktree, no such
 * branch, or git busy.
 */
export const AgentSwitchProblemSchema = z.object({
	reason: z.enum(AGENT_SWITCH_REASONS).catch('unknown'),
	branch: z.string().max(300),
	current: z.string().max(300).nullable(),
	/** All uncommitted changes in the checkout. */
	changes: z.number().int().nonnegative(),
	/** The ones in the way, at most 20: git status' letters and the path. */
	files: z.array(z.object({status: z.string().max(4), path: z.string().max(1000)})).max(20),
	/** The folder name of the worktree that has the branch open. */
	worktree: z.string().max(300).optional(),
});
export type AgentSwitchProblem = z.infer<typeof AgentSwitchProblemSchema>;

export const AgentBranchesSchema = z.object({
	branches: z.array(z.object({name: z.string().max(300)})).max(200),
	/** The branch the project's checkout is on; null when detached or not a git repository. */
	current: z.string().max(300).nullable(),
	/** Asked with switchTo: why the checkout couldn't switch to that branch, when it couldn't. */
	switchProblem: AgentSwitchProblemSchema.optional(),
	/**
	 * Asked with cloudFrom: that branch's commits GitHub doesn't have yet, which a cloud chat wouldn't
	 * see (0 when pushed, null when GitHub has no such branch; konvo-agents 0.54.0+).
	 */
	unpushed: z.object({branch: z.string().max(300), count: z.number().int().min(0).nullable()}).optional(),
});
export type AgentBranches = z.infer<typeof AgentBranchesSchema>;

/** The folders in one folder on a machine, to add a project from (names and paths only, never files). */
export const AgentFoldersSchema = z.object({
	/** The listed folder, absolute. */
	path: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH),
	/** The machine's home folder, so paths can show as ~/… */
	home: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH),
	/** From the root down to path, for breadcrumbs. */
	parts: z.array(z.object({name: z.string().max(300), path: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH)})).max(100),
	folders: z
		.array(
			z.object({
				name: z.string().max(300),
				path: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH),
				/** A git repository (or worktree). */
				git: z.boolean(),
				/** A project is rooted here already. */
				project: z.boolean().optional(),
				/** That project, when it is shared with Konvo. */
				projectId: z.string().max(200).optional(),
			}),
		)
		.max(500),
	/** More folders than were listed. */
	truncated: z.boolean(),
});
export type AgentFolders = z.infer<typeof AgentFoldersSchema>;

/** The GitHub repositories the machine's own `gh` login can clone, most recently updated first. */
export const AgentReposSchema = z.object({
	account: z.string().max(100).nullable(),
	repos: z
		.array(
			z.object({
				/** owner/name */
				name: z.string().max(200),
				private: z.boolean(),
				description: z.string().max(300).optional(),
				updatedAt: z.string().max(40).optional(),
			}),
		)
		.max(300),
});
export type AgentRepos = z.infer<typeof AgentReposSchema>;

const FolderNameSchema = z.string().trim().min(1).max(100);

/** A folder already on the machine, a new empty one (git, first commit), or a GitHub clone into a new one. */
export const AgentAddProjectSchema = z.discriminatedUnion('kind', [
	z.object({kind: z.literal('folder'), path: z.string().min(1).max(AGENT_FOLDER_PATH_MAX_LENGTH)}),
	z.object({
		kind: z.literal('new'),
		parent: z.string().min(1).max(AGENT_FOLDER_PATH_MAX_LENGTH),
		name: FolderNameSchema,
	}),
	z.object({
		kind: z.literal('clone'),
		parent: z.string().min(1).max(AGENT_FOLDER_PATH_MAX_LENGTH),
		name: FolderNameSchema,
		/** owner/name */
		repository: z.string().trim().min(3).max(300),
	}),
]);
export type AgentAddProject = z.infer<typeof AgentAddProjectSchema>;

export const AgentAddedProjectSchema = z.object({
	projectId: z.string().max(200),
	title: z.string().max(200),
	path: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH),
});
export type AgentAddedProject = z.infer<typeof AgentAddedProjectSchema>;

export const AGENT_THREAD_STATUSES = ['running', 'completed', 'idle', 'error', 'interrupted'] as const;
export type AgentThreadStatus = (typeof AGENT_THREAD_STATUSES)[number];

/** A pull request the agent tool links to a thread (konvo-agents 0.7.0+). Konvo reads its live state from GitHub (AgentPullStatus). */
export const AgentPullRequestSchema = z.object({
	/** owner/name */
	repository: z.string().max(200),
	number: z.number().int().positive(),
	url: z
		.string()
		.max(500)
		.regex(/^https:\/\//),
	title: z.string().max(300).optional(),
	state: z.enum(['open', 'closed', 'merged']).optional(),
	draft: z.boolean().optional(),
	/** The pull request the agent tool shows for this thread; listed first by bridges before 0.62.0. */
	linked: z.boolean().optional(),
	/** When it was linked to the thread (konvo-agents 0.62.0+, which lists the newest first). */
	linkedAt: z.string().max(40).optional(),
});
export type AgentPullRequest = z.infer<typeof AgentPullRequestSchema>;

/** owner/name, as GitHub allows them. */
export const AGENT_PULL_REPOSITORY = /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,38})\/[A-Za-z0-9._-]{1,100}$/;
export const AGENT_PULL_LOOKUP_MAX = 20;
export const AgentPullRefSchema = z.object({
	repository: z.string().regex(AGENT_PULL_REPOSITORY),
	number: z.number().int().positive().max(1_000_000_000),
});
export type AgentPullRef = z.infer<typeof AgentPullRefSchema>;
export const AgentPullLookupRequestSchema = z.object({
	pulls: z.array(AgentPullRefSchema).min(1).max(AGENT_PULL_LOOKUP_MAX),
});
export type AgentPullLookupRequest = z.infer<typeof AgentPullLookupRequestSchema>;

/** One check on the pull request's head commit: a GitHub check run or a commit status. */
export const AgentPullCheckSchema = z.object({
	name: z.string().max(300),
	/** 'cancelled': stopped before it finished (by hand or by GitHub); neither passed nor failed. */
	state: z.enum(['passed', 'failed', 'running', 'waiting', 'skipped', 'cancelled']),
	url: z.string().max(1000).optional(),
	/** The check's own one-line result, e.g. "1 test failed". */
	summary: z.string().max(500).optional(),
	startedAt: z.string().max(40).optional(),
	completedAt: z.string().max(40).optional(),
});
export type AgentPullCheck = z.infer<typeof AgentPullCheckSchema>;

export const AgentPullAskedSchema = z.object({
	/** Konvo user ids and display names. */
	by: z.string().max(20),
	byName: z.string().max(100),
	to: z.array(z.object({id: z.string().max(20), name: z.string().max(100)})).max(20),
	at: z.string().max(40),
});
export type AgentPullAsked = z.infer<typeof AgentPullAskedSchema>;

/** One release a merge sets off: a workflow that runs by itself on the push, or one Konvo starts. */
export const AgentReleaseRuleViewSchema = z.object({
	id: z.string().max(40),
	label: z.string().max(80),
	/** 'push': GitHub runs it on the merge; 'dispatch': Konvo starts it once `after` passes on the base branch. */
	kind: z.enum(['push', 'dispatch']),
	workflow: z.string().max(200),
	after: z.string().max(200).optional(),
});
export type AgentReleaseRuleView = z.infer<typeof AgentReleaseRuleViewSchema>;

export const AgentReleaseStepSchema = z.object({
	id: z.string().max(80),
	name: z.string().max(200),
	/** 'cancelled': stopped on purpose with no later run of the same workflow; neither live nor failed. */
	state: z.enum(['passed', 'failed', 'running', 'waiting', 'skipped', 'cancelled']),
	url: z.string().max(1000).optional(),
	/** Why it waits or failed, e.g. "Starts once Tests pass on main". */
	note: z.string().max(300).optional(),
	startedAt: z.string().max(40).optional(),
	completedAt: z.string().max(40).optional(),
});
export type AgentReleaseStep = z.infer<typeof AgentReleaseStepSchema>;

export const AgentPullReleaseSchema = z.object({
	steps: z.array(AgentReleaseStepSchema).max(30),
	/** Every step passed. */
	live: z.boolean(),
	/** A step failed. */
	failed: z.boolean(),
	/** A step was cancelled and nothing ran after it (not a failure). */
	cancelled: z.boolean().optional(),
	/** Who merged it from Konvo and released (absent for merges made elsewhere). */
	by: z.string().max(100).optional(),
});
export type AgentPullRelease = z.infer<typeof AgentPullReleaseSchema>;

/** A pull request as GitHub has it now, read with the viewer's own GitHub sign-in. */
export const AgentPullStatusSchema = z.object({
	repository: z.string().max(200),
	number: z.number().int().positive(),
	url: z.string().max(500),
	title: z.string().max(500),
	state: z.enum(['open', 'closed', 'merged']),
	draft: z.boolean(),
	author: z.string().max(100).optional(),
	/** GitHub logins. */
	assignees: z.array(z.string().max(100)).max(20),
	head: z.string().max(300),
	base: z.string().max(300),
	headSha: z.string().max(64),
	additions: z.number().int().nonnegative(),
	deletions: z.number().int().nonnegative(),
	changedFiles: z.number().int().nonnegative(),
	/** null while GitHub is still working it out. */
	mergeable: z.boolean().nullable(),
	mergedAt: z.string().max(40).optional(),
	mergedBy: z.string().max(100).optional(),
	checks: z.array(AgentPullCheckSchema).max(200),
	/** The squash/merge commit on the base branch, once merged. */
	mergeCommitSha: z.string().max(64).optional(),
	/** The viewer may merge it from Konvo (the repository's merge rights; GitHub still decides too). */
	canMerge: z.boolean().optional(),
	/** Someone asked for it to be merged from Konvo. */
	asked: AgentPullAskedSchema.optional(),
	/** What merging it releases (repositories with release rules), while open. */
	releasePlan: z.array(AgentReleaseRuleViewSchema).max(10).optional(),
	/** After a merge: the runs on the merge commit and what Konvo starts, until live. */
	release: AgentPullReleaseSchema.optional(),
	/** When Konvo read it from GitHub. */
	checkedAt: z.string().max(40),
});
export type AgentPullStatus = z.infer<typeof AgentPullStatusSchema>;

/** How GitHub puts a pull request on its base branch; each repository allows some of them. */
export const AGENT_PULL_MERGE_METHODS = ['squash', 'merge', 'rebase'] as const;
export type AgentPullMergeMethod = (typeof AGENT_PULL_MERGE_METHODS)[number];

export const AgentPullMergeRequestSchema = z.object({
	repository: z.string().regex(AGENT_PULL_REPOSITORY),
	number: z.number().int().positive().max(1_000_000_000),
	/** The head commit it was reviewed at, so a newer push is not merged unseen. */
	headSha: z.string().regex(/^[0-9a-f]{7,64}$/i),
	/** Squash when absent (what older apps send). */
	method: z.enum(AGENT_PULL_MERGE_METHODS).optional(),
	deleteBranch: z.boolean(),
	/** Release rule ids to start after the merge (only 'dispatch' rules need asking). */
	release: z.array(z.string().max(40)).max(10),
});
export type AgentPullMergeRequest = z.infer<typeof AgentPullMergeRequestSchema>;
export const AgentPullMergeResultSchema = z.object({merged: z.boolean(), sha: z.string().max(64).optional()});
export type AgentPullMergeResult = z.infer<typeof AgentPullMergeResultSchema>;

/** A request to merge someone sent you (Ask to merge). */
export const AgentMergeAskSchema = z.object({
	repository: z.string().max(200),
	number: z.number().int().positive(),
	title: z.string().max(500),
	url: z.string().max(500),
	from: z.string().max(20),
	fromName: z.string().max(100),
	at: z.string().max(40),
});
export type AgentMergeAsk = z.infer<typeof AgentMergeAskSchema>;
export const AgentMergeAsksSchema = z.object({asks: z.array(AgentMergeAskSchema).max(100)});

/** Who may merge (and release) from Konvo: everywhere, or per repository instead. Staff edit it. */
export const AgentMergeRightsSchema = z.object({
	/** Konvo user ids. */
	everywhere: z.array(z.string().regex(/^\d{1,20}$/)).max(50),
	/** owner/name (lower case) → user ids; replaces `everywhere` for that repository. */
	repos: z.record(z.string().regex(AGENT_PULL_REPOSITORY), z.array(z.string().regex(/^\d{1,20}$/)).max(50)),
});
export type AgentMergeRights = z.infer<typeof AgentMergeRightsSchema>;

export const AGENT_REVIEW_NOTE_LEVELS = ['block', 'look', 'ok'] as const;
export const AgentReviewNoteSchema = z.object({
	level: z.enum(AGENT_REVIEW_NOTE_LEVELS),
	text: z.string().max(400),
	file: z.string().max(500).optional(),
	/** Konvo's own check (catalogs, versions), not the model's reading. */
	rule: z.boolean().optional(),
});
export type AgentReviewNote = z.infer<typeof AgentReviewNoteSchema>;

/** Konvo's review of a pull request's diff at one head commit. */
export const AgentPullReviewSchema = z.object({
	state: z.enum(['running', 'done', 'failed']),
	headSha: z.string().max(64),
	notes: z.array(AgentReviewNoteSchema).max(20),
	/** Why the model part is missing: no assistant plan connected, or it failed. Konvo's own checks still run. */
	model: z.enum(['done', 'not_connected', 'failed', 'skipped']),
	/** The diff was cut to fit. */
	partial: z.boolean().optional(),
	at: z.string().max(40),
});
export type AgentPullReview = z.infer<typeof AgentPullReviewSchema>;

/**
 * Pull requests looked up together. `github` says whether the viewer's GitHub is connected; pull
 * requests they can't see on GitHub (or that don't exist) are left out.
 */
export const AgentPullLookupSchema = z.object({
	github: z.enum(['connected', 'not_connected']),
	pulls: z.array(AgentPullStatusSchema).max(AGENT_PULL_LOOKUP_MAX),
});
export type AgentPullLookup = z.infer<typeof AgentPullLookupSchema>;

/**
 * A repository's open pull requests as the viewer sees them on GitHub, for the `@` picker (GET
 * /konvo/agents/pulls/open): newest activity first, whether they're asked to review it, and how its
 * checks stand (the first few only).
 */
export const AGENT_OPEN_PULLS_MAX = 30;
export const AgentOpenPullSchema = z.object({
	number: z.number().int().positive(),
	title: z.string().max(500),
	url: z
		.string()
		.max(500)
		.regex(/^https:\/\//),
	/** GitHub login of who opened it. */
	author: z.string().max(100).optional(),
	draft: z.boolean(),
	updatedAt: z.string().max(40),
	/** The viewer is asked to review it. */
	review: z.boolean(),
	/** The head commit's checks summed up; left out when there are none or they weren't read. */
	checks: z.object({state: z.enum(['passed', 'failed', 'running']), failed: z.number().int().nonnegative()}).optional(),
});
export type AgentOpenPull = z.infer<typeof AgentOpenPullSchema>;
export const AgentOpenPullsSchema = z.object({
	github: z.enum(['connected', 'not_connected']),
	repository: z.string().regex(AGENT_PULL_REPOSITORY),
	pulls: z.array(AgentOpenPullSchema).max(AGENT_OPEN_PULLS_MAX),
});
export type AgentOpenPulls = z.infer<typeof AgentOpenPullsSchema>;

/**
 * The Pull requests list in Agents (GET /konvo/agents/pulls/inbox, JP 2026-11-02): every open pull
 * request in the viewer's organisations, and the ones merged in the last week, as GitHub shows them
 * to the viewer. One GitHub search, so each row carries its checks and review state already.
 */
export const AGENT_PULL_INBOX_MAX = 100;
export const AGENT_PULL_INBOX_OWNERS_MAX = 5;
export const AGENT_PULL_MERGE_STATES = [
	'clean',
	'behind',
	'blocked',
	'dirty',
	'unstable',
	'draft',
	'has_hooks',
	'unknown',
] as const;
export type AgentPullMergeState = (typeof AGENT_PULL_MERGE_STATES)[number];
export const AGENT_PULL_REVIEWER_STATES = ['requested', 'approved', 'changes_requested', 'commented'] as const;
export const AGENT_PULL_REVIEWERS_MAX = 6;
export const AgentPullInboxItemSchema = z.object({
	repository: z.string().regex(AGENT_PULL_REPOSITORY),
	number: z.number().int().positive(),
	title: z.string().max(500),
	url: z
		.string()
		.max(500)
		.regex(/^https:\/\//),
	/** GitHub login of who opened it. */
	author: z.string().max(100).optional(),
	state: z.enum(['open', 'merged']),
	draft: z.boolean(),
	createdAt: z.string().max(40),
	updatedAt: z.string().max(40),
	mergedAt: z.string().max(40).optional(),
	head: z.string().max(300),
	base: z.string().max(300),
	additions: z.number().int().nonnegative(),
	deletions: z.number().int().nonnegative(),
	/** The head commit's checks summed up; left out when it has none. */
	checks: z.enum(['passed', 'failed', 'running']).optional(),
	/** GitHub's review decision; left out when the repository asks for none. */
	review: z.enum(['approved', 'changes_requested', 'review_required']).optional(),
	/** The viewer is asked to review it. */
	reviewRequested: z.boolean(),
	/** The viewer opened it. */
	mine: z.boolean(),
	/** Why GitHub would or wouldn't merge it now (GitHub's mergeStateStatus). */
	mergeState: z.enum(AGENT_PULL_MERGE_STATES).optional(),
	labels: z.array(z.string().max(100)).max(10),
	/** Comments on the pull request itself, and who wrote the newest. */
	comments: z.number().int().nonnegative().optional(),
	lastCommenter: z.string().max(100).optional(),
	/** GitHub login of who merged it. */
	mergedBy: z.string().max(100).optional(),
	/** Who reviews it (design/pr-look): each reviewer's latest say, then who is asked and hasn't yet. */
	reviewers: z
		.array(z.object({login: z.string().max(100), state: z.enum(AGENT_PULL_REVIEWER_STATES)}))
		.max(AGENT_PULL_REVIEWERS_MAX)
		.optional(),
});
export type AgentPullInboxItem = z.infer<typeof AgentPullInboxItemSchema>;
export const AgentPullInboxSchema = z.object({
	github: z.enum(['connected', 'not_connected']),
	/** The viewer's GitHub login. */
	viewer: z.string().max(100).optional(),
	/** The organisations and users searched. */
	owners: z.array(z.string().max(100)).max(AGENT_PULL_INBOX_OWNERS_MAX),
	pulls: z.array(AgentPullInboxItemSchema).max(AGENT_PULL_INBOX_MAX),
	/** GitHub pictures of the authors and reviewers, by login, as media proxy addresses (design/pr-look). */
	avatars: z.record(z.string().max(100), z.string().max(1000)).optional(),
	checkedAt: z.string().max(40),
});
export type AgentPullInbox = z.infer<typeof AgentPullInboxSchema>;

/** One entry of a pull request's activity on its page. */
export const AGENT_PULL_EVENT_KINDS = [
	'commit',
	'force_pushed',
	'approved',
	'changes_requested',
	'reviewed',
	'comment',
	'review_requested',
	'ready',
	'draft',
	'merged',
	'closed',
	'reopened',
] as const;
export const AgentPullEventSchema = z.object({
	kind: z.enum(AGENT_PULL_EVENT_KINDS),
	/** GitHub login of who did it. */
	who: z.string().max(100).optional(),
	/** A commit's headline, a comment's first words, or who was asked to review. */
	text: z.string().max(300).optional(),
	at: z.string().max(40),
});
export type AgentPullEvent = z.infer<typeof AgentPullEventSchema>;

/**
 * How to load the pictures and videos a pull request's description (or its comments) shows, by the
 * address the text names (AgentsPullMedia).
 */
const AGENT_PULL_MEDIA_FIELDS = {
	/** Each web picture, as its media proxy address. */
	images: z.record(z.string(), z.string()).optional(),
	/** Pictures dropped in on GitHub that only a GitHub sign-in can see: read through GET /konvo/agents/pulls/media. */
	githubImages: z.array(z.string().max(2048)).max(40).optional(),
	/** Videos dropped in on GitHub (an address on a line of its own): read through GET /konvo/agents/pulls/media. */
	githubVideos: z.array(z.string().max(2048)).max(20).optional(),
};
export const AgentPullMediaSchema = z.object(AGENT_PULL_MEDIA_FIELDS);
export type AgentPullMedia = z.infer<typeof AgentPullMediaSchema>;

/** What a pull request's page shows beyond the card: description, files, commits, activity, merge methods. */
export const AgentPullDetailsSchema = z.object({
	github: z.enum(['connected', 'not_connected']),
	body: z.string().max(20_000),
	...AGENT_PULL_MEDIA_FIELDS,
	labels: z.array(z.object({name: z.string().max(100), color: z.string().max(6)})).max(30),
	/** Logins (and team names) asked to review and yet to answer. */
	requestedReviewers: z.array(z.string().max(100)).max(20),
	/** The methods the repository allows, its default (the viewer's last pick, as on GitHub) first. */
	mergeMethods: z.array(z.enum(AGENT_PULL_MERGE_METHODS)).max(3),
	mergeState: z.enum(AGENT_PULL_MERGE_STATES).optional(),
	review: z.enum(['approved', 'changes_requested', 'review_required']).optional(),
	/** How many commits the base branch has that the pull request lacks; left out when unknown. */
	behindBy: z.number().int().nonnegative().optional(),
	commitCount: z.number().int().nonnegative(),
	/** The newest 100, oldest first. */
	commits: z
		.array(
			z.object({
				sha: z.string().max(64),
				headline: z.string().max(300),
				author: z.string().max(100).optional(),
				at: z.string().max(40),
			}),
		)
		.max(100),
	fileCount: z.number().int().nonnegative(),
	/** The first 100. */
	files: z
		.array(
			z.object({
				path: z.string().max(1000),
				additions: z.number().int().nonnegative(),
				deletions: z.number().int().nonnegative(),
				change: z.enum(['added', 'modified', 'deleted', 'renamed', 'copied', 'changed']),
			}),
		)
		.max(100),
	/** The newest 30, oldest first. */
	/** Its history, oldest first (the newest 300 on a very long pull request). */
	events: z.array(AgentPullEventSchema).max(300),
	/** When it was opened. */
	createdAt: z.string().max(40).optional(),
});
export type AgentPullDetails = z.infer<typeof AgentPullDetailsSchema>;

/**
 * GitHub Actions runs in Agents (Runs, JP 2026-11-02, design/pr-tool 9-11): the person's repositories'
 * recent workflow runs (GET /konvo/agents/runs) and one run's jobs and steps (POST
 * /konvo/agents/runs/detail), read with their own GitHub sign-in.
 */
export const AGENT_RUN_STATES = ['queued', 'running', 'passed', 'failed', 'cancelled', 'skipped'] as const;
export type AgentRunState = (typeof AGENT_RUN_STATES)[number];
export const AGENT_RUNS_MAX = 100;
export const AgentRunSchema = z.object({
	id: z.number().int().positive(),
	repository: z.string().regex(AGENT_PULL_REPOSITORY),
	/** The workflow's name, e.g. "Tests" or "konvo-ship". */
	workflow: z.string().max(200),
	workflowId: z.number().int().nonnegative(),
	/** What GitHub shows for the run: the commit's headline or the run name. */
	title: z.string().max(300),
	number: z.number().int().nonnegative(),
	attempt: z.number().int().positive(),
	/** push, pull_request, workflow_dispatch, schedule, … */
	event: z.string().max(60),
	branch: z.string().max(300),
	sha: z.string().max(64),
	state: z.enum(AGENT_RUN_STATES),
	/** GitHub login of who started it. */
	actor: z.string().max(100).optional(),
	url: z
		.string()
		.max(500)
		.regex(/^https:\/\//),
	createdAt: z.string().max(40),
	startedAt: z.string().max(40).optional(),
	updatedAt: z.string().max(40),
	/** Pull request numbers it ran for. */
	pulls: z.array(z.number().int().positive()).max(10),
	/** Jobs so far (read for running runs and the newest failed ones). */
	jobs: z
		.object({
			done: z.number().int().nonnegative(),
			total: z.number().int().nonnegative(),
			failed: z.number().int().nonnegative(),
			/** The running job's name, or the first failed one's. */
			current: z.string().max(200).optional(),
		})
		.optional(),
});
export type AgentRun = z.infer<typeof AgentRunSchema>;
export const AgentRunsSchema = z.object({
	github: z.enum(['connected', 'not_connected']),
	viewer: z.string().max(100).optional(),
	/** The repositories read, most active first. */
	repositories: z.array(z.string().max(200)).max(20),
	runs: z.array(AgentRunSchema).max(AGENT_RUNS_MAX),
	checkedAt: z.string().max(40),
});
export type AgentRuns = z.infer<typeof AgentRunsSchema>;

export const AgentRunStepSchema = z.object({
	number: z.number().int().nonnegative(),
	name: z.string().max(300),
	state: z.enum(AGENT_RUN_STATES),
	startedAt: z.string().max(40).optional(),
	completedAt: z.string().max(40).optional(),
});
export const AgentRunJobSchema = z.object({
	id: z.number().int().positive(),
	name: z.string().max(300),
	state: z.enum(AGENT_RUN_STATES),
	startedAt: z.string().max(40).optional(),
	completedAt: z.string().max(40).optional(),
	runner: z.string().max(200).optional(),
	url: z.string().max(500).optional(),
	steps: z.array(AgentRunStepSchema).max(100),
	/** The end of a failed job's log. */
	log: z.string().max(8000).optional(),
});
export type AgentRunJob = z.infer<typeof AgentRunJobSchema>;
export const AgentRunRefSchema = z.object({
	repository: z.string().regex(AGENT_PULL_REPOSITORY),
	runId: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
});
export type AgentRunRef = z.infer<typeof AgentRunRefSchema>;
export const AgentRunDetailSchema = z.object({
	github: z.enum(['connected', 'not_connected']),
	run: AgentRunSchema.optional(),
	jobs: z.array(AgentRunJobSchema).max(100),
	/** Earlier runs of the same workflow on the same branch, newest first. */
	earlier: z.array(AgentRunSchema).max(10),
	/** The workflow file, e.g. .github/workflows/konvo-ship.yaml. */
	path: z.string().max(300).optional(),
	/** How long its recent successful runs took (the median). */
	usualMs: z.number().int().nonnegative().optional(),
});
export type AgentRunDetail = z.infer<typeof AgentRunDetailSchema>;
export const AGENT_RUN_ACTIONS = ['rerun', 'rerun_failed', 'cancel'] as const;
export const AgentRunActionRequestSchema = AgentRunRefSchema.extend({action: z.enum(AGENT_RUN_ACTIONS)});
export type AgentRunActionRequest = z.infer<typeof AgentRunActionRequestSchema>;

/** A comment on a pull request or in one of its review threads, as GitHub shows it to the viewer. */
export const AgentPullCommentSchema = z.object({
	id: z.string().max(200),
	author: z.string().max(100),
	avatarUrl: z.string().url().max(500).optional(),
	body: z.string().max(4000),
	createdAt: z.string().max(40),
	url: z.string().url().max(500).optional(),
});
export type AgentPullComment = z.infer<typeof AgentPullCommentSchema>;

/** A review thread on a line of the diff (GitHub's review comments, design/agents-t3-parity review). */
export const AgentPullThreadSchema = z.object({
	/** GitHub's node id, for replying and resolving. */
	id: z.string().max(200),
	path: z.string().max(1000),
	line: z.number().int().positive().optional(),
	resolved: z.boolean(),
	/** The lines it was on have changed since. */
	outdated: z.boolean(),
	canResolve: z.boolean(),
	canReply: z.boolean(),
	/** The lines of the diff it is on, as GitHub cuts them (its first comment's diff hunk). */
	hunk: z.string().max(3000).optional(),
	comments: z.array(AgentPullCommentSchema).min(1).max(20),
});
export type AgentPullThread = z.infer<typeof AgentPullThreadSchema>;

export const AGENT_PULL_REVIEW_STATES = ['approved', 'changes_requested', 'commented', 'dismissed'] as const;

/** A pull request's conversation for the Agents PR panel: review threads, comments, who approved or asked for changes. */
export const AgentPullConversationSchema = z.object({
	github: z.enum(['connected', 'not_connected']),
	threads: z.array(AgentPullThreadSchema).max(100),
	/** Comments on the pull request itself, oldest first (the newest 100). */
	comments: z.array(AgentPullCommentSchema).max(100),
	/** Reviews that came with words (an approval's note, what should change), oldest first (the newest 50). */
	reviews: z
		.array(
			z.object({
				id: z.string().max(200),
				author: z.string().max(100),
				avatarUrl: z.string().url().max(500).optional(),
				state: z.enum(AGENT_PULL_REVIEW_STATES),
				body: z.string().max(4000),
				createdAt: z.string().max(40),
			}),
		)
		.max(50)
		.optional(),
	/** Each reviewer's latest say. */
	reviewers: z
		.array(
			z.object({
				login: z.string().max(100),
				avatarUrl: z.string().url().max(500).optional(),
				state: z.enum(AGENT_PULL_REVIEW_STATES),
			}),
		)
		.max(30),
	draft: z.boolean(),
	/** GitHub merges it by itself once its checks pass. */
	autoMerge: z.boolean(),
	/** The base branch moved on; Update the branch brings it in. */
	behind: z.boolean(),
	/** The viewer may update the branch. */
	canUpdateBranch: z.boolean(),
	/** The pictures and videos its comments show. */
	...AGENT_PULL_MEDIA_FIELDS,
});
export type AgentPullConversation = z.infer<typeof AgentPullConversationSchema>;

export const AGENT_PULL_ACTIONS = [
	'reply',
	'resolve',
	'unresolve',
	'comment',
	'approve',
	'request_changes',
	'auto_merge',
	'cancel_auto_merge',
	'ready',
	'draft',
	'update_branch',
	'revert',
	'close',
	'reopen',
] as const;
export type AgentPullAction = (typeof AGENT_PULL_ACTIONS)[number];

/** Something the person does to a pull request from the Agents PR panel, as themselves on GitHub. */
export const AgentPullActionRequestSchema = AgentPullRefSchema.extend({
	action: z.enum(AGENT_PULL_ACTIONS),
	/** The review thread, for reply, resolve and unresolve. */
	threadId: z.string().max(200).optional(),
	/** The words, for reply, comment and request_changes (approve takes an optional note). */
	body: z.string().max(20_000).optional(),
});
export type AgentPullActionRequest = z.infer<typeof AgentPullActionRequestSchema>;

/** Done; `url` is the new pull request a revert opened. */
export const AgentPullActionResultSchema = z.object({ok: z.literal(true), url: z.string().url().max(500).optional()});
export type AgentPullActionResult = z.infer<typeof AgentPullActionResultSchema>;

/** What a turn changed in the worktree, on the reply that ends it (konvo-agents 0.7.0+). */
export const AgentTurnChangesSchema = z.object({
	/** The tool's turn count for the turn, for the diff op (konvo-agents 0.25.0+). */
	turn: z.number().int().positive().optional(),
	files: z.number().int().nonnegative(),
	additions: z.number().int().nonnegative(),
	deletions: z.number().int().nonnegative(),
	/** The first 100 files. */
	list: z
		.array(
			z.object({
				path: z.string().max(1000),
				kind: z.string().max(20),
				additions: z.number().int().nonnegative(),
				deletions: z.number().int().nonnegative(),
			}),
		)
		.max(100),
});
export type AgentTurnChanges = z.infer<typeof AgentTurnChangesSchema>;

export const AgentThreadSummarySchema = z.object({
	id: z.string().max(200),
	projectId: z.string().max(200),
	/** As plain text: a title named after a prompt's first line loses its Markdown (AgentTitle.ts). */
	title: z.string().max(500).transform(plainAgentTitle),
	status: z.enum(AGENT_THREAD_STATUSES),
	needsYou: z.boolean(),
	provider: z.string().max(40),
	/**
	 * The chat runs in the cloud (Claude Code on the web); url opens it there and branch is where the
	 * cloud pushes, both on the thread itself rather than the list (konvo-agents 0.54.0+).
	 */
	cloud: z
		.object({
			kind: z.literal('claude'),
			url: z.string().max(300).url().optional(),
			branch: z.string().max(300).optional(),
		})
		.optional(),
	model: z.string().max(120).optional(),
	/** The thread's model, reasoning level and service tier as a choice (konvo-agents 0.13.0+). */
	modelChoice: AgentModelChoiceSchema.optional(),
	branch: z.string().max(300).optional(),
	runtimeMode: z.string().max(40).optional(),
	updatedAt: z.string().max(40),
	/** When the agent tool settled the thread; not when its work finished (that is turnFinishedAt). */
	settledAt: z.string().max(40).optional(),
	/** In the agent tool's Settled section: finished work put away (konvo-agents 0.6.0+; older bridges: settledAt is set). */
	settled: z.boolean().optional(),
	/** Snoozed in the agent tool until then (T3's own snooze; only while still ahead; konvo-agents 0.57.0+). */
	snoozedUntil: z.string().max(40).optional(),
	/** When the thread was archived: only on archived threads (the archived list, or one opened from it; konvo-agents 0.36.0+). */
	archivedAt: z.string().max(40).optional(),
	/** The latest turn: when it started and, once over, when it finished (konvo-agents 0.5.0+). */
	turnStartedAt: z.string().max(40).optional(),
	turnFinishedAt: z.string().max(40).optional(),
	/**
	 * Subagents or background jobs ('working'), or only monitors ('monitoring'), still running after
	 * the turn: the main agent isn't done yet (konvo-agents 0.16.0+).
	 */
	background: z.enum(['working', 'monitoring']).optional(),
	/** The latest turn was started by the agent itself, when background work came back (konvo-agents 0.16.0+). */
	wake: z.boolean().optional(),
	/** What a needs-you thread waits on (konvo-agents 0.5.0+). */
	waitingOn: z.enum(['approval', 'question', 'plan']).optional(),
	/** The agent tool's last error, when the thread stopped on one (konvo-agents 0.5.0+). */
	error: z.string().max(300).optional(),
	/**
	 * It stopped because the person's plan ran out (konvo-agents 0.10.0+): `usage` until the limit
	 * resets (resetsAt when the tool says; otherwise the plan's used-up window tells), or `credits`.
	 */
	limit: z.object({kind: z.enum(['usage', 'credits']), resetsAt: z.string().max(40).optional()}).optional(),
	pullRequests: z.array(AgentPullRequestSchema).max(10).optional(),
});
export type AgentThreadSummary = z.infer<typeof AgentThreadSummarySchema>;

/** A file or image attached to a message in the agent tool. The bytes are fetched on demand (op `attachment`). */
export const AgentAttachmentSchema = z.object({
	id: z.string().max(300),
	kind: z.enum(['image', 'file']),
	name: z.string().max(300),
	mimeType: z.string().max(200),
	size: z.number().int().nonnegative(),
	/**
	 * A page the agent showed in the chat with T3's html_render tool (konvo-agents 0.52.0+): an HTML
	 * file of the reply it belongs above. `height` is the frame height the agent asked for.
	 */
	render: z.object({height: z.number().int().positive().max(100_000).optional()}).optional(),
});
export type AgentAttachment = z.infer<typeof AgentAttachmentSchema>;
/** Largest attachment the bridge sends through Konvo. */
export const AGENT_ATTACHMENT_MAX_BYTES = 15 * 1024 * 1024;
/**
 * Largest attachment or reply video the bridge hands over by download link (konvo-agents 0.20.0+):
 * T3 Code's own file limit. Such files skip the JSON results; see AgentsDownloads in the API.
 */
export const AGENT_ATTACHMENT_LINK_MAX_BYTES = 50 * 1024 * 1024;
/** Files over this go by download link when the bridge can; smaller ones stay inline in the result. */
export const AGENT_DOWNLOAD_INLINE_MAX_BYTES = 4 * 1024 * 1024;
export const AGENT_DOWNLOAD_ID_PATTERN = /^ad_[a-f0-9]{32}$/;
/** Longest file path a reply's picture or video link may have (op `media`; T3's own limit). */
export const AGENT_MEDIA_PATH_MAX_LENGTH = 1024;

/**
 * Pictures and files sent from Konvo with one prompt (konvo-agents 0.3.0+). The bridge uploads
 * each to the agent tool; T3 Code itself takes up to 100.
 */
export const AGENT_UPLOAD_MAX_FILES = 20;
/** What a bridge without can.uploadLinks takes: it gets every file inline in one command. */
export const AGENT_UPLOAD_INLINE_MAX_FILES = 4;
/** All the pictures of one prompt together (T3 Code's own limit). */
export const AGENT_UPLOAD_IMAGES_TOTAL_MAX_BYTES = 80 * 1024 * 1024;
/** Largest file (T3 Code's own limit is 50 MiB). */
export const AGENT_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;
/** Largest picture: T3 Code refuses bigger ones, so the app shrinks them first. */
export const AGENT_UPLOAD_IMAGE_MAX_BYTES = 10 * 1024 * 1024;
/** Largest file sent inline as base64 (in a request or a command); bigger ones go by upload id. */
export const AGENT_UPLOAD_INLINE_MAX_BYTES = 10 * 1024 * 1024;
/** Pictures the agent tools take inline; other files go as files. */
export const AGENT_UPLOAD_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
export const isAgentUploadImage = (mimeType: string) =>
	(AGENT_UPLOAD_IMAGE_TYPES as ReadonlyArray<string>).includes(mimeType.toLowerCase());
export const AgentUploadSchema = z.object({
	name: z.string().trim().min(1).max(255),
	mimeType: z.string().max(100),
	/** Base64. */
	data: z
		.string()
		.min(1)
		.max(Math.ceil(AGENT_UPLOAD_INLINE_MAX_BYTES / 3) * 4 + 4),
});
export type AgentUpload = z.infer<typeof AgentUploadSchema>;
export const AGENT_UPLOAD_ID_PATTERN = /^au_[a-f0-9]{32}$/;
/** POST /konvo/agents/uploads: the file's bytes are the body, its name is the `name` query. */
export const AgentUploadedSchema = z.object({
	uploadId: z.string().regex(AGENT_UPLOAD_ID_PATTERN),
	name: z.string().max(255),
	mimeType: z.string().max(100),
	size: z.number().int().nonnegative(),
});
export type AgentUploaded = z.infer<typeof AgentUploadedSchema>;
/** A file in a send or start request: inline, or uploaded first (POST /konvo/agents/uploads). */
export const AgentUploadInputSchema = z.union([
	AgentUploadSchema,
	z.object({uploadId: z.string().regex(AGENT_UPLOAD_ID_PATTERN)}),
]);
export type AgentUploadInput = z.infer<typeof AgentUploadInputSchema>;
export const AgentQuestionUploadsSchema = z
	.record(z.string().min(1).max(200), z.array(AgentUploadInputSchema))
	.refine((byQuestion) => Object.values(byQuestion).flat().length <= AGENT_UPLOAD_MAX_FILES, {
		message: 'Too many question attachments.',
	});
export type AgentQuestionUploads = z.infer<typeof AgentQuestionUploadsSchema>;
/** A file in a command, inline or (for a bridge with can.uploadLinks) fetched by the bridge from GET /konvo/agents/bridge/uploads/:uploadId. */
export const AgentCommandUploadSchema = z.union([AgentUploadSchema, AgentUploadedSchema]);
export type AgentCommandUpload = z.infer<typeof AgentCommandUploadSchema>;

/** One answered question, shown instead of the plain text the agent tool records for it. */
export const AgentAnswerSchema = z.object({
	question: z.string().max(5000),
	answer: z.string().max(20_000),
});
export type AgentAnswer = z.infer<typeof AgentAnswerSchema>;

/** running → done | failed | stopped. Codex subagents that wait to be resumed count as done. */
export const AGENT_SUBAGENT_STATUSES = ['running', 'done', 'failed', 'stopped'] as const;
export type AgentSubagentStatus = (typeof AGENT_SUBAGENT_STATUSES)[number];

/** A subagent the main agent started (Claude's Agent tool, Codex's spawned agents, T3's delegated tasks), as the agent tool records it. */
export const AgentSubagentSchema = z.object({
	id: z.string().max(200),
	title: z.string().max(500),
	/** The kind of subagent, e.g. "Explore" or "general-purpose". */
	role: z.string().max(200).optional(),
	model: z.string().max(200).optional(),
	/** What runs it when that differs from its chat, e.g. "codex" for a task a Claude chat delegated (konvo-agents 0.56.0+). */
	provider: z.string().max(100).optional(),
	status: z.enum(AGENT_SUBAGENT_STATUSES),
	startedAt: z.string().max(40),
	endedAt: z.string().max(40).optional(),
	/** What it is doing now, e.g. "Running Get line numbers for report" (while running). */
	step: z.string().max(1000).optional(),
	toolUses: z.number().int().nonnegative().optional(),
	durationMs: z.number().int().nonnegative().optional(),
	/** The task the main agent gave it (clipped). */
	prompt: z.string().max(20_000).optional(),
	/** What it reported back (clipped). */
	result: z.string().max(20_000).optional(),
	/** Its latest update in its own words, read from its transcript (konvo-agents 0.17.0+). */
	note: z.string().max(1000).optional(),
	/** The newest pictures it looked at, oldest first; op `subagentPicture` serves them (konvo-agents 0.17.0+). */
	pictures: z.array(z.string().max(1024)).max(3).optional(),
});
export type AgentSubagent = z.infer<typeof AgentSubagentSchema>;

export const AGENT_SUBAGENT_STEP_KINDS = [
	'command',
	'read',
	'picture',
	'edit',
	'search',
	'web',
	'agent',
	'other',
] as const;
export type AgentSubagentStepKind = (typeof AGENT_SUBAGENT_STEP_KINDS)[number];

/** One tool call of a subagent, as its transcript records it. */
export const AgentSubagentStepSchema = z.object({
	id: z.string().max(200),
	kind: z.enum(AGENT_SUBAGENT_STEP_KINDS),
	/** The tool's name, e.g. "Bash". */
	tool: z.string().max(100),
	/** A command's description (else its first line), a path, a pattern or a URL. */
	label: z.string().max(300),
	/** The full command. */
	detail: z.string().max(2000).optional(),
	/** The start of what it returned (commands and failures). */
	output: z.string().max(1500).optional(),
	/** For reads, pictures and edits. */
	path: z.string().max(1024).optional(),
	at: z.string().max(40),
	running: z.boolean().optional(),
	failed: z.boolean().optional(),
});
export type AgentSubagentStep = z.infer<typeof AgentSubagentStepSchema>;

export const AgentSubagentSessionItemSchema = z.discriminatedUnion('kind', [
	/** The task the main agent gave it (first), and anything the main agent sent it later. */
	z.object({
		kind: z.literal('message'),
		id: z.string().max(200),
		text: z.string().max(20_000),
		createdAt: z.string().max(40),
	}),
	/** The subagent's own words; the last one, once it ends, is what it reported. */
	z.object({
		kind: z.literal('update'),
		id: z.string().max(200),
		text: z.string().max(20_000),
		createdAt: z.string().max(40),
	}),
	/** The tool calls between two updates. */
	z.object({
		kind: z.literal('steps'),
		id: z.string().max(200),
		createdAt: z.string().max(40),
		steps: z.array(AgentSubagentStepSchema).max(200),
		/** Older steps of this run left out. */
		dropped: z.number().int().nonnegative().optional(),
	}),
]);
export type AgentSubagentSessionItem = z.infer<typeof AgentSubagentSessionItemSchema>;

/** A subagent's own session, read live from its transcript on the machine (op `subagent`, konvo-agents 0.17.0+). */
export const AgentSubagentSessionSchema = z.object({
	/** Items before the first one sent, left out. */
	earlier: z.number().int().nonnegative(),
	items: z.array(AgentSubagentSessionItemSchema).max(300),
});
export type AgentSubagentSession = z.infer<typeof AgentSubagentSessionSchema>;

export const AGENT_TOOL_STEP_KINDS = [
	'command',
	'read',
	'picture',
	'edit',
	'search',
	'web',
	'mcp',
	'thought',
	'other',
] as const;
export type AgentToolStepKind = (typeof AGENT_TOOL_STEP_KINDS)[number];

/** Most steps a run of tool activity carries; a longer run keeps its newest (konvo-agents MAX_STEPS). */
export const AGENT_TOOL_STEPS_MAX = 100;

/** One thing the agent did in a run of tool activity: a tool call or a thought (konvo-agents 0.47.0+). */
export const AgentToolStepSchema = z.object({
	id: z.string().max(200),
	kind: z.enum(AGENT_TOOL_STEP_KINDS),
	/** The tool's name, e.g. "Bash" or "mcp__t3-code__preview_open". */
	tool: z.string().max(100).optional(),
	/** A command, a path, a pattern or a URL; a thought's first line. */
	label: z.string().max(300),
	/** A thought's text (clipped). */
	detail: z.string().max(1200).optional(),
	/** The first meaningful line of what it returned, or "N lines", as the agent tool sums it up. */
	output: z.string().max(200).optional(),
	/** The files it read or changed. */
	paths: z.array(z.string().max(1024)).max(12).optional(),
	at: z.string().max(40),
	running: z.boolean().optional(),
	failed: z.boolean().optional(),
	declined: z.boolean().optional(),
});
export type AgentToolStep = z.infer<typeof AgentToolStepSchema>;

export const AGENT_PLAN_STEP_STATUSES = ['pending', 'running', 'done'] as const;

/** The agent's to-do list (Claude's TodoWrite, Codex's plan), as it stood at the end of a run (konvo-agents 0.47.0+). */
export const AgentToolPlanSchema = z.object({
	steps: z
		.array(z.object({text: z.string().max(500), status: z.enum(AGENT_PLAN_STEP_STATUSES)}))
		.min(1)
		.max(30),
	explanation: z.string().max(1000).optional(),
});
export type AgentToolPlan = z.infer<typeof AgentToolPlanSchema>;

/** Mentions one prompt may carry. */
export const AGENT_MENTIONS_MAX = 10;
/** Konvo's summary of a mentioned chat on another machine, for the agent. */
export const AGENT_MENTION_SUMMARY_MAX = 6000;

/**
 * Another chat or a pull request mentioned in a prompt, sent to the bridge (konvo-agents 0.57.0+).
 * The prompt links to it as `[label](mention:<ref>)`. A chat on another machine carries Konvo's
 * summary of it (that agent can't read it); one on the same machine doesn't.
 */
export const AgentMentionSchema = z.discriminatedUnion('kind', [
	z.object({
		ref: z.string().regex(/^[a-z0-9_-]{1,40}$/i),
		kind: z.literal('chat'),
		threadId: z.string().max(200),
		title: z.string().max(200),
		machineId: z.string().max(200).optional(),
		machine: z.string().max(200).optional(),
		summary: z.string().max(AGENT_MENTION_SUMMARY_MAX).optional(),
		link: z.string().max(500).optional(),
	}),
	z.object({
		ref: z.string().regex(/^[a-z0-9_-]{1,40}$/i),
		kind: z.literal('pull'),
		repo: z.string().max(200),
		number: z.number().int().positive(),
		title: z.string().max(300),
		url: z.string().max(500),
		head: z.string().max(200).optional(),
		base: z.string().max(200).optional(),
		state: z.enum(['open', 'closed', 'merged']).optional(),
		draft: z.boolean().optional(),
	}),
]);
export type AgentMention = z.infer<typeof AgentMentionSchema>;

/** A mention in a prompt read back from the agent tool; machineId only for a chat on another machine. */
export const AgentMentionRefSchema = z.discriminatedUnion('kind', [
	z.object({
		kind: z.literal('chat'),
		threadId: z.string().max(200),
		title: z.string().max(200),
		machineId: z.string().max(200).optional(),
	}),
	z.object({
		kind: z.literal('pull'),
		number: z.number().int().positive(),
		title: z.string().max(300),
		url: z.string().max(500),
		repo: z.string().max(200).optional(),
	}),
]);
export type AgentMentionRef = z.infer<typeof AgentMentionRefSchema>;

export const AgentThreadItemSchema = z.discriminatedUnion('kind', [
	z.object({
		kind: z.literal('user'),
		id: z.string().max(200),
		text: z.string().max(200_000),
		createdAt: z.string().max(40),
		/** Set when the prompt came from Konvo: the sender's name. */
		via: z.string().max(200).optional(),
		attachments: z.array(AgentAttachmentSchema).max(50).optional(),
		/** Set when this message is the person's answer to the agent's questions (konvo-agents 0.5.0+). */
		answers: z.array(AgentAnswerSchema).max(20).optional(),
		/** Chats and pull requests the prompt mentions; the text reads them as `[label](mention:<n>)` (konvo-agents 0.57.0+). */
		mentions: z.array(AgentMentionRefSchema).max(AGENT_MENTIONS_MAX).optional(),
	}),
	z.object({
		kind: z.literal('assistant'),
		id: z.string().max(200),
		text: z.string().max(200_000),
		createdAt: z.string().max(40),
		streaming: z.boolean().optional(),
		attachments: z.array(AgentAttachmentSchema).max(50).optional(),
		changes: AgentTurnChangesSchema.optional(),
	}),
	z.object({
		kind: z.literal('tools'),
		id: z.string().max(200),
		createdAt: z.string().max(40),
		commands: z.number().int().nonnegative(),
		fileChanges: z.number().int().nonnegative(),
		other: z.number().int().nonnegative(),
		running: z.boolean(),
		/** What the agent did, oldest first (konvo-agents 0.47.0+). */
		steps: z.array(AgentToolStepSchema).max(AGENT_TOOL_STEPS_MAX).optional(),
		/** Older steps of this run left out of `steps`. */
		droppedSteps: z.number().int().nonnegative().optional(),
		/** The agent's to-do list at the end of this run (konvo-agents 0.47.0+). */
		plan: AgentToolPlanSchema.optional(),
	}),
	/** The subagents one turn started, placed where the first one started (konvo-agents 0.12.0+). */
	z.object({
		kind: z.literal('subagents'),
		id: z.string().max(200),
		createdAt: z.string().max(40),
		agents: z.array(AgentSubagentSchema).min(1).max(50),
	}),
	/** Something that happened to the chat itself: the agent compacting its context (konvo-agents 0.47.0+). */
	z.object({
		kind: z.literal('note'),
		id: z.string().max(200),
		createdAt: z.string().max(40),
		note: z.enum(['compacted']),
		beforeTokens: z.number().int().nonnegative().max(1_000_000_000).optional(),
		afterTokens: z.number().int().nonnegative().max(1_000_000_000).optional(),
		/** The agent tool compacted by itself, not on request. */
		auto: z.boolean().optional(),
	}),
]);
export type AgentThreadItem = z.infer<typeof AgentThreadItemSchema>;

export const AGENT_DECISIONS = ['accept', 'acceptForSession', 'decline', 'cancel'] as const;
export type AgentDecision = (typeof AGENT_DECISIONS)[number];

/** A prompt the agent tool holds until the running turn ends. `id` is what the queued op takes. */
export const AgentQueuedPromptSchema = z.object({
	id: z.string().max(200),
	messageId: z.string().max(200).optional(),
	attachments: z.array(AgentAttachmentSchema).max(50).optional(),
	text: z.string().max(20_000),
	createdAt: z.string().max(40),
	via: z.string().max(200).optional(),
});
export type AgentQueuedPrompt = z.infer<typeof AgentQueuedPromptSchema>;

export const AgentApprovalSchema = z.object({
	requestId: z.string().max(200),
	/** The step that asked (a tool step's id in the thread's items; konvo-agents 0.58.0+). */
	stepId: z.string().max(200).optional(),
	kind: z.string().max(60),
	/** The exact command or change. Always shown in full. */
	detail: z.string().max(50_000),
	createdAt: z.string().max(40),
	decisions: z.array(z.enum(AGENT_DECISIONS)).max(4),
});
export type AgentApproval = z.infer<typeof AgentApprovalSchema>;

/** A question the agent asks the person (T3's user-input request; Claude's AskUserQuestion, Codex's request_user_input). */
export const AgentQuestionSchema = z.object({
	id: z.string().max(200),
	/** Short label, e.g. "Auth method". Often just "Question". */
	header: z.string().max(200),
	question: z.string().max(5000),
	/** May be empty: then only a written answer fits. */
	options: z
		.array(
			z.object({label: z.string().max(500), description: z.string().max(2000), value: z.string().max(500).optional()}),
		)
		.max(20),
	multiSelect: z.boolean(),
	/** False when only the options are allowed. */
	allowCustomAnswer: z.boolean(),
});
export type AgentQuestion = z.infer<typeof AgentQuestionSchema>;

export const AgentQuestionRequestSchema = z.object({
	requestId: z.string().max(300),
	createdAt: z.string().max(40),
	questions: z.array(AgentQuestionSchema).min(1).max(10),
	/**
	 * The agent's turn is paused on the answer, so dismissing the question stops the turn. Left out:
	 * the agent asked and moved on; dismissing just closes it (konvo-agents 0.36.0+).
	 */
	blocking: z.literal(true).optional(),
});
export type AgentQuestionRequest = z.infer<typeof AgentQuestionRequestSchema>;

/**
 * The agent asks for a secret (T3's request_secret: a key, password or private address), shown above
 * the composer (konvo-agents 0.53.0+). The secret goes to the agent tool on the machine, which keeps
 * it out of the chat; Konvo only passes it on.
 */
export const AgentSecretRequestSchema = z.object({
	requestId: z.string().max(300),
	createdAt: z.string().max(40),
	/** What is asked for, e.g. "Zoho MCP Server URL". */
	label: z.string().max(300),
	/** Why the agent needs it and where to find it. */
	reason: z.string().max(2000).optional(),
	/** What it looks like, e.g. "https://….zohomcp.com/mcp/…". */
	placeholder: z.string().max(300).optional(),
});
export type AgentSecretRequest = z.infer<typeof AgentSecretRequestSchema>;

/** The answer to a secret request: the secret, or no. */
export const AgentSecretAnswerSchema = z.discriminatedUnion('type', [
	z.object({type: z.literal('save'), secret: z.string().trim().min(1).max(10_000)}),
	z.object({type: z.literal('decline')}),
]);
export type AgentSecretAnswer = z.infer<typeof AgentSecretAnswerSchema>;

/** Answers by question id: the written answer or chosen option value, or several values for multi-select. */
export const AgentAnswersSchema = z.record(
	z.string().max(200),
	z.union([z.string().max(10_000), z.array(z.string().max(500)).max(20)]),
);
export type AgentAnswers = z.infer<typeof AgentAnswersSchema>;

/**
 * How full a thread's context window is, as the agent tool reports it (konvo-agents 0.36.0+; T3's
 * `context-window.updated` activity or protocol 2 `tokenUsage`). About this chat's memory, not the
 * plan's usage limits (AgentPlanUsageSchema).
 */
export const AgentContextUsageSchema = z.object({
	/** Tokens in the context now. */
	usedTokens: z.number().int().nonnegative().max(1_000_000_000),
	/** The model's context window, when the tool knows it. */
	maxTokens: z.number().int().positive().max(1_000_000_000).optional(),
	/** The tool compacts the context by itself when it fills up. */
	autoCompact: z.boolean().optional(),
	/** ...at this many tokens. */
	autoCompactAt: z.number().int().positive().max(1_000_000_000).optional(),
	updatedAt: z.string().max(40).optional(),
	/** The latest time the agent compacted its context in the recent turns. */
	compacted: z
		.object({
			at: z.string().max(40),
			beforeTokens: z.number().int().nonnegative().max(1_000_000_000).optional(),
			afterTokens: z.number().int().nonnegative().max(1_000_000_000).optional(),
		})
		.optional(),
});
export type AgentContextUsage = z.infer<typeof AgentContextUsageSchema>;

export const AgentThreadDetailSchema = AgentThreadSummarySchema.extend({
	items: z.array(AgentThreadItemSchema).max(2000),
	approvals: z.array(AgentApprovalSchema).max(50),
	/** Questions waiting for an answer (konvo-agents 0.5.0+; older bridges leave it out). */
	questions: z.array(AgentQuestionRequestSchema).max(20).optional(),
	/** Secrets the agent waits for (konvo-agents 0.53.0+; left out when there are none). */
	secrets: z.array(AgentSecretRequestSchema).max(10).optional(),
	/** Prompts waiting behind the running turn, first to go first (can.queue; konvo-agents 0.58.0+). */
	queued: z.array(AgentQueuedPromptSchema).max(20).optional(),
	revision: z.string().max(100),
	page: z.object({hasMore: z.boolean(), beforeCursor: z.string().max(2000).optional()}),
	/** The tool's turn count of the latest turn it can diff, for the whole thread's changes (konvo-agents 0.25.0+). */
	diffTurn: z.number().int().positive().optional(),
	/**
	 * Set when the chat can be rewound to before one of its prompts (konvo-agents 0.47.0+); `files`:
	 * its files can go back too (it works in its own worktree, not the project folder).
	 */
	rewind: z.object({files: z.boolean()}).optional(),
	/** How full the context window is (konvo-agents 0.36.0+; left out when the tool doesn't say). */
	contextUsage: AgentContextUsageSchema.optional(),
	/** The newest project the agent made in this chat, while it is still apart from the chat's own (konvo-agents 0.61.0+). */
	madeProject: z
		.object({
			projectId: z.string().max(200),
			title: z.string().max(200),
			path: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH),
		})
		.optional(),
});
export type AgentThreadDetail = z.infer<typeof AgentThreadDetailSchema>;

// ---------------------------------------------------------------------------
// Ask a colleague: the owner sends the agent's question into their DM with someone, who answers
// it from a card there. The answer goes to the owner's machine as the owner's (send limit).
// ---------------------------------------------------------------------------

/** The card's embed provider in the DM (KonvoCard draws AgentAskCard; older apps a plain card). */
export const AGENT_ASK_PROVIDER = 'Konvo:agent_question';
export const AGENT_ASK_NOTE_MAX = 1000;
/** A written answer from someone else ends with this, so the agent knows whose it is. */
export const agentAskSignature = (name: string) => ` (answered by ${name})`;

/** waiting → answered (by the person) | owner (the owner answered first) | closed (the agent moved on). */
export const AGENT_ASK_STATUSES = ['waiting', 'answered', 'owner', 'closed'] as const;
export type AgentAskStatus = (typeof AGENT_ASK_STATUSES)[number];

export const AgentAskSchema = z.object({
	askId: z.string().regex(/^[a-f0-9]{32}$/),
	machineId: z.string().max(20),
	threadId: z.string().max(200),
	requestId: z.string().max(300),
	ownerId: z.string().max(20),
	ownerName: z.string().max(200),
	personId: z.string().max(20),
	personName: z.string().max(200),
	projectTitle: z.string().max(200).optional(),
	threadTitle: z.string().max(500),
	/** What runs the thread (claudeAgent, codex): the card shows its logo. */
	provider: z.string().max(40).optional(),
	note: z.string().max(AGENT_ASK_NOTE_MAX).optional(),
	questions: z.array(AgentQuestionSchema).min(1).max(10),
	/** Of the questions as asked: an answer must name the same questions. */
	questionsHash: z.string().max(100),
	createdAt: z.string().max(40),
	status: z.enum(AGENT_ASK_STATUSES),
	answeredAt: z.string().max(40).optional(),
	/** What the person answered, as shown (option labels, their written text). */
	answer: z.array(AgentAnswerSchema).max(20).optional(),
	/** The DM and its card. */
	channelId: z.string().max(20),
	messageId: z.string().max(20).optional(),
});
export type AgentAsk = z.infer<typeof AgentAskSchema>;

/** POST /konvo/agents/machines/:id/threads/:threadId/questions/:requestId/ask */
export const AgentAskRequestSchema = z.object({
	personId: z.string().regex(/^\d{1,20}$/),
	note: z.string().trim().max(AGENT_ASK_NOTE_MAX).optional(),
});
export type AgentAskRequest = z.infer<typeof AgentAskRequestSchema>;

/** GET /konvo/agents/asks/:askId: the owner or the asked person sees it. */
export const AgentAskViewSchema = z.object({ask: AgentAskSchema, role: z.enum(['owner', 'person'])});
export type AgentAskView = z.infer<typeof AgentAskViewSchema>;

/** POST /konvo/agents/asks/:askId/answer: only the asked person, for the questions they saw. */
export const AgentAskAnswerRequestSchema = z.object({
	answers: AgentAnswersSchema,
	questionsHash: z.string().max(100),
});
export type AgentAskAnswerRequest = z.infer<typeof AgentAskAnswerRequestSchema>;

// ---------------------------------------------------------------------------
// Bridge <-> API wire protocol (bridge authenticates with its machine token)
// ---------------------------------------------------------------------------

export const AGENTS_PROTOCOL_VERSION = 1;

/** Why a machine's agent tool can't be reached (T3 not running, unsupported version, ...), in the machine's words. */
export const AgentToolProblemSchema = z.object({
	code: z.string().max(40),
	message: z.string().max(400),
});
export type AgentToolProblem = z.infer<typeof AgentToolProblemSchema>;

export const AgentMachineInfoSchema = z.object({
	name: z.string().trim().min(1).max(80),
	platform: z.enum(['linux', 'darwin', 'win32', 'other']),
	bridgeVersion: z.string().max(40),
	protocol: z.number().int().positive(),
	tools: z.array(AgentToolInfoSchema).max(8),
	limits: AgentLimitsSchema,
	/** Only the projects the owner shares. */
	projects: z.array(AgentProjectSchema).max(500),
	/** Bridge 0.34.0+: set while `tools` is empty. */
	toolProblem: AgentToolProblemSchema.optional(),
});
export type AgentMachineInfo = z.infer<typeof AgentMachineInfoSchema>;

/** POST /konvo/agents/pair (no auth; the one-time code is the credential). */
export const AgentPairRequestSchema = z.object({
	code: z.string().trim().min(4).max(20),
	machine: AgentMachineInfoSchema,
});
export const AgentPairResponseSchema = z.object({machineId: z.string(), token: z.string(), name: z.string()});

/** Longest thread title Konvo sends when renaming one. */
export const AGENT_THREAD_TITLE_MAX = 200;

export const AGENT_OPS = [
	'commands',
	'resetUsage',
	'agentSetup',
	'agentUpdate',
	'account',
	'deviceAccess',
	'threads',
	'thread',
	'watch',
	'send',
	'start',
	'interrupt',
	'compact',
	'restartSession',
	'rewind',
	'settle',
	'snooze',
	'queued',
	'stopDevServer',
	'rename',
	'archive',
	'delete',
	'archived',
	'respond',
	'attachment',
	'answer',
	'dismiss',
	'secret',
	'media',
	'branches',
	'push',
	'cloudHandoff',
	'toProject',
	'document',
	'documentAsset',
	'subagent',
	'subagentPicture',
	'folders',
	'repos',
	'addProject',
	'renameProject',
	'removeProject',
	'diff',
	'search',
	'fileRefs',
	'fileList',
	'fileSearch',
	'fileText',
	'file',
	'browserTunnel',
	'devServers',
	'machineStats',
	'terminalTunnel',
] as const;
export type AgentOp = (typeof AGENT_OPS)[number];

// ---------------------------------------------------------------------------
// Files (konvo-agents 0.26.0+): the files an agent's replies name, and the thread's project tree.
// The bridge serves a file only when it is inside the thread's worktree or project folder, or (for
// the machine's owner only) when a reply in that thread names it. Secrets never leave the machine
// (konvo_agents/src/paths.ts isSecretPath). Teammates a thread is shared with get `scope`: 'chat'
// (watch, comment) reaches only files the chat named or changed; 'project' (prompt) the whole tree.
// ---------------------------------------------------------------------------

/** A small version of a picture: `tile` for a grid, `view` to fill a screen. */
export const AgentPictureSizeSchema = z.enum(['tile', 'view']);
export type AgentPictureSize = z.infer<typeof AgentPictureSizeSchema>;

/** Largest file Konvo opens from a machine (the download link limit). */
export const AGENT_FILE_MAX_BYTES = AGENT_ATTACHMENT_LINK_MAX_BYTES;
/** Largest text file shown in the code view. */
export const AGENT_FILE_TEXT_MAX_BYTES = 2 * 1024 * 1024;
/** Paths resolved in one fileRefs call. */
export const AGENT_FILE_REFS_MAX = 200;
/** Entries in one fileList page; the rest come with `offset` ("Show more"). */
export const AGENT_FILE_LIST_PAGE = 200;
export const AGENT_FILE_SEARCH_MAX = 50;
export const AGENT_FILE_PATH_MAX_LENGTH = AGENT_MEDIA_PATH_MAX_LENGTH;

/** How much of the machine a caller reaches: a teammate with watch or comment only what the chat named or changed. */
export const AGENT_FILE_SCOPES = ['chat', 'project'] as const;
export type AgentFileScope = (typeof AGENT_FILE_SCOPES)[number];

/** Git's word for a file against the last commit: modified, added (staged or new), deleted, renamed, untracked. */
export const AGENT_FILE_STATUSES = ['modified', 'added', 'deleted', 'renamed', 'untracked'] as const;
export type AgentFileStatus = (typeof AGENT_FILE_STATUSES)[number];

/** The folder a thread's files are listed from: its worktree, else its project folder. */
export const AgentFileRootSchema = z.object({
	/** The folder's own name, e.g. "konvo". */
	name: z.string().max(300),
	kind: z.enum(['worktree', 'project']),
	/** The checked-out branch, when it is a git repository. */
	branch: z.string().max(300).optional(),
	/** Whether it is a git repository (status marks, ignored files and the name index come from git). */
	git: z.boolean(),
});
export type AgentFileRoot = z.infer<typeof AgentFileRootSchema>;

/** One file or folder in a listing or a search. */
export const AgentFileEntrySchema = z.object({
	name: z.string().max(300),
	/** Relative to the root, '/'-separated. */
	path: z.string().max(AGENT_FILE_PATH_MAX_LENGTH),
	kind: z.enum(['file', 'dir']),
	/** Bytes (files). */
	size: z.number().int().nonnegative().optional(),
	/** When it last changed, in ms since 1970 (konvo-agents 0.69.0+). */
	modified: z.number().int().nonnegative().optional(),
	/** A picture's pixel size as it is shown (konvo-agents 0.69.0+). */
	width: z.number().int().positive().optional(),
	height: z.number().int().positive().optional(),
	status: z.enum(AGENT_FILE_STATUSES).optional(),
	/** A folder holding changed files. */
	changed: z.boolean().optional(),
	/** Git ignores it (node_modules, build output, caches): shown greyed, never listed or opened from the tree. */
	ignored: z.boolean().optional(),
	/** On the secrets deny list (.env, keys, credentials): never opened from Konvo. */
	secret: z.boolean().optional(),
});
export type AgentFileEntry = z.infer<typeof AgentFileEntrySchema>;

/** GET .../files: one folder's entries, folders first, then by name. */
export const AgentFileListSchema = z.object({
	root: AgentFileRootSchema,
	/** The folder, relative to the root ('' is the root). */
	path: z.string().max(AGENT_FILE_PATH_MAX_LENGTH),
	entries: z.array(AgentFileEntrySchema).max(AGENT_FILE_LIST_PAGE),
	/** Entries in the folder in all. */
	total: z.number().int().nonnegative(),
	/** More entries after these: ask again with offset. */
	more: z.boolean().optional(),
});
export type AgentFileList = z.infer<typeof AgentFileListSchema>;

/** What a path a reply names is on the machine; `found` false when it isn't there (or may not be shown). */
export const AgentFileRefSchema = z.object({
	/** The path as asked for (as the reply wrote it, line suffix removed). */
	ref: z.string().max(AGENT_FILE_PATH_MAX_LENGTH),
	found: z.boolean(),
	/** Relative to the root when under it, else absolute (outside paths as written). Use it for the other file ops. */
	path: z.string().max(AGENT_FILE_PATH_MAX_LENGTH).optional(),
	kind: z.enum(['file', 'dir']).optional(),
	size: z.number().int().nonnegative().optional(),
	/** A picture: when it last changed, in ms since 1970, and its pixel size as it is shown (konvo-agents 0.69.0+). */
	modified: z.number().int().nonnegative().optional(),
	width: z.number().int().positive().optional(),
	height: z.number().int().positive().optional(),
	status: z.enum(AGENT_FILE_STATUSES).optional(),
	/** Not in the thread's worktree or project: a reply named it (the owner only). */
	outside: z.boolean().optional(),
	secret: z.boolean().optional(),
	ignored: z.boolean().optional(),
	/** A folder: how many files and folders are directly in it (at most 1000). */
	count: z.number().int().nonnegative().optional(),
	/** A bare name or partial path that matches more than one file (not found). */
	ambiguous: z.boolean().optional(),
});
export type AgentFileRef = z.infer<typeof AgentFileRefSchema>;

export const AgentFileRefsSchema = z.object({
	root: AgentFileRootSchema,
	refs: z.array(AgentFileRefSchema).max(AGENT_FILE_REFS_MAX),
});
export type AgentFileRefs = z.infer<typeof AgentFileRefsSchema>;

/** GET .../files/search: files whose name (then path) holds the words, best first. */
export const AgentFileSearchSchema = z.object({
	matches: z.array(AgentFileEntrySchema).max(AGENT_FILE_SEARCH_MAX),
	/** Files the name index holds (git's list, or a capped walk outside git). */
	indexed: z.number().int().nonnegative(),
	truncated: z.boolean().optional(),
});
export type AgentFileSearch = z.infer<typeof AgentFileSearchSchema>;

/** GET .../files/text: a text file for the code view. */
export const AgentFileTextSchema = z.object({
	name: z.string().max(300),
	path: z.string().max(AGENT_FILE_PATH_MAX_LENGTH),
	size: z.number().int().nonnegative().max(AGENT_FILE_TEXT_MAX_BYTES),
	text: z.string().max(AGENT_FILE_TEXT_MAX_BYTES),
});
export type AgentFileText = z.infer<typeof AgentFileTextSchema>;

/** Largest patch of one file the bridge sends (konvo_agents/src/diffs.ts). */
export const AGENT_DIFF_FILE_MAX_BYTES = 512 * 1024;
/** Patches in one diff answer; the rest are asked for one file at a time. */
export const AGENT_DIFF_BUDGET_BYTES = 1.5 * 1024 * 1024;
export const AGENT_DIFF_MAX_FILES = 2000;

/** One file of what a turn or a thread changed (konvo-agents 0.25.0+). */
export const AgentDiffFileSchema = z.object({
	path: z.string().max(1000),
	oldPath: z.string().max(1000).optional(),
	kind: z.enum(['added', 'deleted', 'modified', 'renamed']),
	additions: z.number().int().nonnegative(),
	deletions: z.number().int().nonnegative(),
	binary: z.boolean().optional(),
	/** The file's part of the unified diff, from its `diff --git` line. */
	patch: z.string().max(AGENT_DIFF_FILE_MAX_BYTES).optional(),
	/** Left out: too big to show, or over the answer's budget (ask for the file by path). */
	omitted: z.enum(['large', 'budget']).optional(),
});
export type AgentDiffFile = z.infer<typeof AgentDiffFileSchema>;

/** What turns `from` (exclusive) to `to` changed; from 0 is the whole thread. */
export const AgentDiffSchema = z.object({
	from: z.number().int().nonnegative(),
	to: z.number().int().positive(),
	files: z.array(AgentDiffFileSchema).max(AGENT_DIFF_MAX_FILES),
	/** More files changed than AGENT_DIFF_MAX_FILES. */
	truncated: z.boolean().optional(),
});
export type AgentDiff = z.infer<typeof AgentDiffSchema>;

export const AGENT_SEARCH_MAX_MATCHES = 50;
/** A thread message that matches a search, as the agent tool finds it (konvo-agents 0.25.0+). */
export const AgentSearchMatchSchema = z.object({
	threadId: z.string().max(200),
	projectId: z.string().max(200),
	source: z.enum(['user', 'assistant']),
	snippet: z.string().max(240),
	at: z.string().max(40).optional(),
});
export type AgentSearchMatch = z.infer<typeof AgentSearchMatchSchema>;
export const AgentSearchSchema = z.object({matches: z.array(AgentSearchMatchSchema).max(AGENT_SEARCH_MAX_MATCHES)});
export type AgentSearch = z.infer<typeof AgentSearchSchema>;

/** Largest document the bridge reads for Konvo (Markdown and HTML files a reply names). */
export const AGENT_DOCUMENT_MAX_BYTES = 2 * 1024 * 1024;

/** A Markdown document or HTML page an agent's reply names, read from the machine. */
export const AgentDocumentSchema = z.object({
	name: z.string().max(300),
	kind: z.enum(['html', 'markdown']),
	size: z.number().int().nonnegative().max(AGENT_DOCUMENT_MAX_BYTES),
	text: z.string().max(AGENT_DOCUMENT_MAX_BYTES),
	/** Added by Konvo: each web picture the document names, as its media proxy address. */
	images: z.record(z.string(), z.string()).optional(),
});
export type AgentDocument = z.infer<typeof AgentDocumentSchema>;

/** Web pictures signed in one request. */
export const AGENT_WEB_PICTURES_MAX = 40;
/** Longest web picture address Konvo signs. */
export const AGENT_WEB_PICTURE_URL_MAX = 2048;

/**
 * Web pictures an agent's HTML page loads, including the ones its script sets as it runs
 * (`img.src = LIB + path`), which only the page frame sees.
 */
export const AgentWebPicturesRequestSchema = z
	.object({
		urls: z
			.array(
				z
					.string()
					.max(AGENT_WEB_PICTURE_URL_MAX)
					.regex(/^https?:\/\//i),
			)
			.min(1)
			.max(AGENT_WEB_PICTURES_MAX),
	})
	.strict();
export type AgentWebPicturesRequest = z.infer<typeof AgentWebPicturesRequestSchema>;
/** Each web picture, as its media proxy address; ones that can't be signed are left out. */
export const AgentWebPicturesSchema = z.object({images: z.record(z.string(), z.string())});
export type AgentWebPictures = z.infer<typeof AgentWebPicturesSchema>;

/** Set by Konvo on a teammate's file op: the bridge then refuses outside paths and, for 'chat', anything the chat didn't name or change. */
const fileCaller = {
	teammate: z.literal(true).optional(),
	scope: z.enum(AGENT_FILE_SCOPES).optional(),
};

export const AgentCommandSchema = z.discriminatedUnion('op', [
	z.object({id: z.string(), op: z.literal('commands'), args: AgentCommandsRequestSchema}),
	z.object({id: z.string(), op: z.literal('resetUsage'), args: AgentUsageResetRequestSchema}),
	z.object({id: z.string(), op: z.literal('agentSetup'), args: AgentSetupRequestSchema}),
	/** Starts updating Claude Code or Codex and answers at once with the tool showing it queued or running (konvo-agents 0.40.0+, can.updateAgents; older bridges answer UNKNOWN_OP). Needs the send limit. */
	z.object({id: z.string(), op: z.literal('agentUpdate'), args: AgentUpdateRequestSchema}),
	/** Add, edit or remove a Claude or Codex account (konvo-agents 0.51.0+). Owner only. */
	z.object({id: z.string(), op: z.literal('account'), args: AgentAccountRequestSchema}),
	/** Turns Agent device access on or off in the agent tool (konvo-agents 0.68.0+, can.deviceAccess). Owner only; needs the send limit. */
	z.object({id: z.string(), op: z.literal('deviceAccess'), args: AgentDeviceAccessRequestSchema}),
	z.object({id: z.string(), op: z.literal('threads'), args: z.object({})}),
	z.object({
		id: z.string(),
		op: z.literal('thread'),
		args: z.object({
			threadId: z.string().max(200),
			turns: z.number().int().min(1).max(50).optional(),
			before: z.string().max(2000).optional(),
			/** Asked for by a teammate the thread is shared with (team sharing, konvo-agents 0.25.0+): the bridge checks its own share limit. */
			teammate: z.literal(true).optional(),
		}),
	}),
	/** Threads Konvo wants live updates for; replaces the previous set, expires after 90 s unless renewed. */
	z.object({id: z.string(), op: z.literal('watch'), args: z.object({threadIds: z.array(z.string().max(200)).max(10)})}),
	z.object({
		id: z.string(),
		op: z.literal('send'),
		args: z.object({
			sendId: z.string().uuid().optional(),
			threadId: z.string().max(200),
			/** May be empty when pictures or files carry the message. */
			prompt: z.string().max(20_000),
			sender: z.string().max(200),
			steer: z.boolean(),
			attachments: z.array(AgentCommandUploadSchema).max(AGENT_UPLOAD_MAX_FILES).optional(),
			/** Switch the thread to another model of the same provider (needs can.model). */
			model: AgentModelChoiceSchema.optional(),
			/** A teammate's prompt: the bridge marks it "[Sent from Konvo by <sender>]" so the agent and the thread show whose it is. */
			teammate: z.literal(true).optional(),
			/** Chats and pull requests the prompt mentions (needs can.mentions). */
			mentions: z.array(AgentMentionSchema).max(AGENT_MENTIONS_MAX).optional(),
			/** Wait behind the running turn instead of steering into it (needs can.queue). */
			queue: z.boolean().optional(),
		}),
	}),
	z.object({
		id: z.string(),
		op: z.literal('start'),
		args: z.object({
			sendId: z.string().uuid().optional(),
			projectId: z.string().max(200),
			prompt: z.string().min(1).max(20_000),
			sender: z.string().max(200),
			title: z.string().max(120).optional(),
			worktree: z.boolean().optional(),
			branch: z.string().max(200).optional(),
			/** Puts the checkout's changes aside (git stash) before switching it to branch (needs can.checkoutHelp). */
			stash: z.boolean().optional(),
			/** Runs the chat in Claude's cloud, from branch as GitHub has it, in a worktree (needs can.cloud). */
			cloud: z.boolean().optional(),
			planMode: z.boolean().optional(),
			/** Left out: the project's model, then the machine default (needs can.model). */
			model: AgentModelChoiceSchema.optional(),
			/** Pictures and files with the first prompt (needs can.startFiles). */
			attachments: z.array(AgentCommandUploadSchema).max(AGENT_UPLOAD_MAX_FILES).optional(),
			/** Chats and pull requests the prompt mentions (needs can.mentions). */
			mentions: z.array(AgentMentionSchema).max(AGENT_MENTIONS_MAX).optional(),
		}),
	}),
	z.object({
		id: z.string(),
		op: z.literal('interrupt'),
		args: z.object({threadId: z.string().max(200), teammate: z.literal(true).optional()}),
	}),
	/** Compacts the chat's context: a /compact turn (konvo-agents 0.47.0+, can.compact). Needs the send limit. */
	z.object({id: z.string(), op: z.literal('compact'), args: z.object({threadId: z.string().max(200)})}),
	/** Restarts the chat's agent process; the chat carries on (konvo-agents 0.47.0+, can.restartSession). Needs the send limit. */
	z.object({id: z.string(), op: z.literal('restartSession'), args: z.object({threadId: z.string().max(200)})}),
	/**
	 * Rewinds the chat to before one of its prompts: it and all after leave the chat and the agent's
	 * memory; restoreFiles puts the worktree's files back too (konvo-agents 0.47.0+, can.rewind).
	 * Stops a working agent first. Needs the send limit; the owner's only.
	 */
	z.object({
		id: z.string(),
		op: z.literal('rewind'),
		args: z.object({threadId: z.string().max(200), messageId: z.string().max(200), restoreFiles: z.boolean()}),
	}),
	/** A shared project's local branches, newest commit first (konvo-agents 0.11.0+). */
	z.object({
		id: z.string(),
		op: z.literal('branches'),
		args: z.object({
			projectId: z.string().max(200),
			switchTo: z.string().max(300).optional(),
			/** Also counts that branch's commits GitHub doesn't have (konvo-agents 0.54.0+, can.cloud). */
			cloudFrom: z.string().max(300).optional(),
		}),
	}),
	/** A chat's folder against GitHub; with push, its work in progress on a hand-off branch (konvo-agents 0.55.0+, can.cloudHandoff). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('cloudHandoff'),
		args: z.object({threadId: z.string().max(200), push: z.boolean()}),
	}),
	/**
	 * A project for a chat in a folder of its own (konvo-agents 0.61.0+, can.chatToProject). With move, a No
	 * project chat moves there with its files; without, the project is found or made to continue the chat in.
	 * Needs the send limit.
	 */
	z.object({
		id: z.string(),
		op: z.literal('toProject'),
		args: AgentToProjectArgsSchema,
	}),
	/** Pushes a branch to GitHub, never forced, before a cloud chat starts from it (konvo-agents 0.54.0+, can.cloud). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('push'),
		args: z.object({projectId: z.string().max(200), branch: z.string().min(1).max(300)}),
	}),
	/** Moves a thread into the agent tool's Settled section or back out (konvo-agents 0.6.0+; older bridges answer UNKNOWN_OP). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('settle'),
		args: z.object({threadId: z.string().max(200), settled: z.boolean()}),
	}),
	/** Snoozes a thread in the agent tool until a time, or wakes it with null (konvo-agents 0.57.0+, can.snooze). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('snooze'),
		args: z.object({threadId: z.string().max(200), until: z.string().max(40).nullable()}),
	}),
	/** Takes a queued prompt out, or sends it into the running turn now (konvo-agents 0.58.0+, can.queue). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('queued'),
		args: z.object({threadId: z.string().max(200), runId: z.string().max(200), action: z.enum(['cancel', 'steer'])}),
	}),
	/** Ctrl+C in the chat's terminal that runs a dev server (konvo-agents 0.58.0+, can.stopDevServer). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('stopDevServer'),
		args: z.object({threadId: z.string().max(200), terminalId: z.string().max(200)}),
	}),
	/** Gives a thread a new title in the agent tool (konvo-agents 0.24.0+; older bridges answer UNKNOWN_OP). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('rename'),
		args: z.object({threadId: z.string().max(200), title: z.string().min(1).max(AGENT_THREAD_TITLE_MAX)}),
	}),
	/** Archives a thread, or brings it back (konvo-agents 0.36.0+, can.archive; older bridges answer UNKNOWN_OP). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('archive'),
		args: z.object({threadId: z.string().max(200), archived: z.boolean()}),
	}),
	/** Deletes a thread from the agent tool for good (konvo-agents 0.36.0+, can.delete). Needs the send limit. */
	z.object({id: z.string(), op: z.literal('delete'), args: z.object({threadId: z.string().max(200)})}),
	/** The archived threads, newest archived first (konvo-agents 0.36.0+, can.archive). */
	z.object({id: z.string(), op: z.literal('archived'), args: z.object({})}),
	z.object({
		id: z.string(),
		op: z.literal('respond'),
		args: z.object({
			threadId: z.string().max(200),
			requestId: z.string().max(200),
			decision: z.enum(AGENT_DECISIONS),
			teammate: z.literal(true).optional(),
		}),
	}),
	/** One attachment's bytes (added in konvo-agents 0.2.0; older bridges answer UNKNOWN_OP). */
	z.object({
		id: z.string(),
		op: z.literal('attachment'),
		args: z.object({
			threadId: z.string().max(200),
			attachmentId: z.string().max(300),
			/** Konvo takes big files by download link (konvo-agents 0.20.0+; older bridges ignore it). */
			link: z.boolean().optional(),
		}),
	}),
	/** Answers the agent's questions (added in konvo-agents 0.5.0; older bridges answer UNKNOWN_OP). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('answer'),
		args: z.object({
			threadId: z.string().max(200),
			requestId: z.string().max(300),
			answers: AgentAnswersSchema,
			attachmentsByQuestionId: z.record(z.string().max(200), z.array(AgentCommandUploadSchema)).optional(),
			teammate: z.literal(true).optional(),
		}),
	}),
	/** Closes the agent's question without an answer (konvo-agents 0.36.0+, can.dismissQuestion; older bridges answer UNKNOWN_OP). Needs the send limit. A question the turn waits on stops the turn. */
	z.object({
		id: z.string(),
		op: z.literal('dismiss'),
		args: z.object({
			threadId: z.string().max(200),
			requestId: z.string().max(300),
			teammate: z.literal(true).optional(),
		}),
	}),
	/** Answers the agent's request for a secret, or declines it (konvo-agents 0.53.0+, can.secretRequests). The owner's only; needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('secret'),
		args: z.object({
			threadId: z.string().max(200),
			requestId: z.string().max(300),
			answer: AgentSecretAnswerSchema,
		}),
	}),
	/** A picture or video on the machine that a reply in the thread links by path, e.g. `![shot](/abs/shot.png)` (konvo-agents 0.7.0+; older bridges answer UNKNOWN_OP). The bridge refuses paths no reply links. */
	z.object({
		id: z.string(),
		op: z.literal('media'),
		args: z.object({
			threadId: z.string().max(200),
			path: z.string().min(1).max(AGENT_MEDIA_PATH_MAX_LENGTH),
			/** Konvo takes big files by download link (konvo-agents 0.20.0+; older bridges ignore it). */
			link: z.boolean().optional(),
			/** A small version of a picture (konvo-agents 0.69.0+; older bridges send the original). */
			picture: AgentPictureSizeSchema.optional(),
		}),
	}),
	/** A Markdown document or HTML page a reply names, as text (konvo-agents 0.12.0+; older bridges answer UNKNOWN_OP). */
	z.object({
		id: z.string(),
		op: z.literal('document'),
		args: z.object({threadId: z.string().max(200), path: z.string().min(1).max(AGENT_MEDIA_PATH_MAX_LENGTH)}),
	}),
	/** A picture, stylesheet, font or video such an HTML page loads from beside it (konvo-agents 0.18.0+; older bridges answer UNKNOWN_OP). */
	z.object({
		id: z.string(),
		op: z.literal('documentAsset'),
		args: z.object({
			threadId: z.string().max(200),
			path: z.string().min(1).max(AGENT_MEDIA_PATH_MAX_LENGTH),
			asset: z.string().min(1).max(AGENT_MEDIA_PATH_MAX_LENGTH),
		}),
	}),
	/** One subagent's own session from its transcript (konvo-agents 0.17.0+; older bridges answer UNKNOWN_OP, T3 protocol 2 NO_SESSION). */
	z.object({
		id: z.string(),
		op: z.literal('subagent'),
		args: z.object({threadId: z.string().max(200), subagentId: z.string().max(200)}),
	}),
	/** A picture that subagent looked at (konvo-agents 0.17.0+). The bridge refuses any other path. */
	z.object({
		id: z.string(),
		op: z.literal('subagentPicture'),
		args: z.object({
			threadId: z.string().max(200),
			subagentId: z.string().max(200),
			path: z.string().min(1).max(AGENT_MEDIA_PATH_MAX_LENGTH),
		}),
	}),
	/** The folders in a folder on the machine, to add a project from; `~` is home, left out is home (konvo-agents 0.22.0+). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('folders'),
		args: z.object({path: z.string().max(AGENT_FOLDER_PATH_MAX_LENGTH).optional()}),
	}),
	/** GitHub repositories the machine's `gh` can clone (konvo-agents 0.22.0+, needs tools[0].github). Needs the send limit. */
	z.object({id: z.string(), op: z.literal('repos'), args: z.object({})}),
	/** Adds a project to the agent tool; it is shared with Konvo straight away (konvo-agents 0.22.0+). Needs the send limit. */
	z.object({id: z.string(), op: z.literal('addProject'), args: AgentAddProjectSchema}),
	/** Renames a shared project in the agent tool (konvo-agents 0.22.0+). Needs the send limit. */
	z.object({
		id: z.string(),
		op: z.literal('renameProject'),
		args: z.object({projectId: z.string().max(200), title: z.string().trim().min(1).max(200)}),
	}),
	/** Removes a shared project from the agent tool with its chats; the folder stays (konvo-agents 0.22.0+). Needs the send limit. */
	z.object({id: z.string(), op: z.literal('removeProject'), args: z.object({projectId: z.string().max(200)})}),
	/** What turns `from` (exclusive) to `to` changed, from 0 the whole thread; `file` asks for one file's patch (konvo-agents 0.25.0+). A read. */
	z.object({
		id: z.string(),
		op: z.literal('diff'),
		args: z.object({
			threadId: z.string().max(200),
			from: z.number().int().nonnegative(),
			to: z.number().int().positive(),
			file: z.string().max(1000).optional(),
		}),
	}),
	/** Messages in the machine's shared threads that match the query (konvo-agents 0.25.0+). A read. */
	z.object({
		id: z.string(),
		op: z.literal('search'),
		args: z.object({
			query: z.string().trim().min(2).max(200),
			limit: z.number().int().min(1).max(AGENT_SEARCH_MAX_MATCHES).optional(),
		}),
	}) /** Where paths a reply names are on the machine (konvo-agents 0.26.0+). A read. */,
	z.object({
		id: z.string(),
		op: z.literal('fileRefs'),
		args: z.object({
			threadId: z.string().max(200),
			refs: z.array(z.string().min(1).max(AGENT_FILE_PATH_MAX_LENGTH)).min(1).max(AGENT_FILE_REFS_MAX),
			...fileCaller,
		}),
	}),
	/** One folder of the thread's worktree or project, `path` relative to it ('' the root), from `offset` (konvo-agents 0.26.0+). */
	z.object({
		id: z.string(),
		op: z.literal('fileList'),
		args: z.object({
			threadId: z.string().max(200),
			path: z.string().max(AGENT_FILE_PATH_MAX_LENGTH),
			offset: z.number().int().nonnegative().max(100_000).optional(),
			...fileCaller,
		}),
	}),
	/** Files in the thread's worktree or project by name (konvo-agents 0.26.0+). Not for scope 'chat'. */
	z.object({
		id: z.string(),
		op: z.literal('fileSearch'),
		args: z.object({
			threadId: z.string().max(200),
			query: z.string().trim().min(1).max(200),
			limit: z.number().int().min(1).max(AGENT_FILE_SEARCH_MAX).optional(),
			...fileCaller,
		}),
	}),
	/** A text file for the code view, up to AGENT_FILE_TEXT_MAX_BYTES (konvo-agents 0.26.0+). */
	z.object({
		id: z.string(),
		op: z.literal('fileText'),
		args: z.object({
			threadId: z.string().max(200),
			path: z.string().min(1).max(AGENT_FILE_PATH_MAX_LENGTH),
			...fileCaller,
		}),
	}),
	/** A file's bytes, up to AGENT_FILE_MAX_BYTES; `link` as for media (konvo-agents 0.26.0+). */
	z.object({
		id: z.string(),
		op: z.literal('file'),
		args: z.object({
			threadId: z.string().max(200),
			path: z.string().min(1).max(AGENT_FILE_PATH_MAX_LENGTH),
			link: z.boolean().optional(),
			/** A small version of a picture (konvo-agents 0.69.0+; older bridges send the original). */
			picture: AgentPictureSizeSchema.optional(),
			...fileCaller,
		}),
	}),
	/** Dial Konvo's browser relay for this session (AgentBrowser.ts). Owner only. */
	z.object({id: z.string(), op: z.literal('browserTunnel'), args: AgentBrowserTunnelArgsSchema}),
	/** The dev servers running on the machine; `threadId` marks that thread's own. A read. */
	z.object({id: z.string(), op: z.literal('devServers'), args: AgentDevServersArgsSchema}),
	z.object({id: z.string(), op: z.literal('machineStats'), args: AgentMachineStatsArgsSchema}),
	/** Dial Konvo's terminal relay for this session (AgentTerminals.ts terminal sessions). Owner only. */
	z.object({id: z.string(), op: z.literal('terminalTunnel'), args: AgentTerminalTunnelArgsSchema}),
]);
export type AgentCommand = z.infer<typeof AgentCommandSchema>;

/** POST /konvo/agents/bridge/poll -> commands for this machine (long-poll, up to ~25 s). */
export const AgentPollResponseSchema = z.object({
	commands: z.array(AgentCommandSchema).max(50),
	/** The konvo-agents version this Konvo serves: a bridge that is older updates now, not at its hourly check. */
	bridgeVersion: z.string().max(40).optional(),
});

export const AgentErrorSchema = z.object({
	code: z.string().max(60),
	message: z.string().max(1000),
	/** SWITCH_FAILED: why, from git (konvo-agents 0.41.0+). */
	switchProblem: AgentSwitchProblemSchema.optional(),
});
export type AgentError = z.infer<typeof AgentErrorSchema>;

// ---------------------------------------------------------------------------
// Health and activity: what went wrong on a machine and why, in Settings and Team insights.
// ---------------------------------------------------------------------------

export const AGENT_ACTIVITY_LEVELS = ['info', 'warn', 'error'] as const;
export type AgentActivityLevel = (typeof AGENT_ACTIVITY_LEVELS)[number];

/**
 * What happened. The app words each kind itself (translated); `detail` carries the machine's or the
 * agent tool's own words, which stay as they are.
 */
export const AGENT_ACTIVITY_KINDS = [
	/** The machine reached Konvo again; `seconds` = how long it was away. */
	'connected',
	/** The machine stopped reaching Konvo (no poll for a while). */
	'disconnected',
	/** konvo-agents now runs `version` (was `from`). */
	'bridgeUpdated',
	/** konvo-agents couldn't update itself to `version`; `detail` = why (e.g. npm EACCES). */
	'bridgeUpdateFailed',
	/** The agent tool (T3 Code) stopped answering; `detail` = the tool problem. */
	'toolDown',
	/** The agent tool answers again; `seconds` = how long it was down. */
	'toolUp',
	/** The agent tool itself now runs `version` (was `from`). */
	'toolUpdated',
	/** An account (`account` = display name, `provider` = instance id) needs signing in again. */
	'signedOut',
	/** An account is signed in and ready again. */
	'signedIn',
	/** Claude Code or Codex (`provider`) updated to `version`. */
	'agentUpdated',
	/** Claude Code or Codex (`provider`) couldn't update; `detail` = why. */
	'agentUpdateFailed',
	/** Something asked from Konvo failed: `op`, `code`, `threadId`/`threadTitle`, `detail` = the message. */
	'actionFailed',
	/** An account was added, changed or removed from Konvo (`account`, `provider`). */
	'accountAdded',
	'accountChanged',
	'accountRemoved',
] as const;
export type AgentActivityKind = (typeof AGENT_ACTIVITY_KINDS)[number];

export const AgentActivityEventSchema = z.object({
	/** Unique per machine; also the `ref` an error answer carries, so a failed send links to its event. */
	id: z.string().max(40),
	at: z.string().max(40),
	level: z.enum(AGENT_ACTIVITY_LEVELS),
	kind: z.enum(AGENT_ACTIVITY_KINDS),
	/** The same event again within 10 minutes counts up instead of adding a row; `lastAt` = the newest. */
	count: z.number().int().positive().optional(),
	lastAt: z.string().max(40).optional(),
	op: z.string().max(40).optional(),
	code: z.string().max(60).optional(),
	threadId: z.string().max(200).optional(),
	threadTitle: z.string().max(200).optional(),
	/** Instance id of the account / provider concerned. */
	provider: z.string().max(80).optional(),
	account: z.string().max(80).optional(),
	version: z.string().max(40).optional(),
	from: z.string().max(40).optional(),
	seconds: z.number().int().nonnegative().optional(),
	/** The machine's or tool's own words (never chat text, file contents or secrets). */
	detail: z.string().max(400).optional(),
	/** Versions when it happened: konvo-agents, agent tool, platform. */
	bridgeVersion: z.string().max(40).optional(),
	toolVersion: z.string().max(40).optional(),
	/** Set when a later event settled it (signed in again, back online, the retry went through). */
	resolvedAt: z.string().max(40).optional(),
});
export type AgentActivityEvent = z.infer<typeof AgentActivityEventSchema>;

/** Kept per machine for this long (Team insights counts the last 7 and 28 days). */
export const AGENT_ACTIVITY_KEEP_DAYS = 28;
export const AGENT_ACTIVITY_MAX_EVENTS = 500;

/** POST /konvo/agents/bridge/events `activity` (konvo-agents 0.51.0+): what only the machine sees. */
export const AgentBridgeActivitySchema = z.object({
	kind: z.enum(['bridgeUpdateFailed', 'toolDown', 'toolUp', 'connected']),
	at: z.string().max(40),
	version: z.string().max(40).optional(),
	seconds: z.number().int().nonnegative().optional(),
	detail: z.string().max(400).optional(),
});
export type AgentBridgeActivity = z.infer<typeof AgentBridgeActivitySchema>;

export const AGENT_HEALTH_STATES = ['ok', 'warn', 'bad', 'off'] as const;
export type AgentHealthState = (typeof AGENT_HEALTH_STATES)[number];

/** One line of a machine's health, worked out by the API from what the machine reports. */
export const AgentHealthCheckSchema = z.object({
	/** 'konvo' | 'bridge' | 'tool' | `account:<instance id>`. */
	id: z.string().max(100),
	kind: z.enum(['konvo', 'bridge', 'tool', 'account']),
	state: z.enum(AGENT_HEALTH_STATES),
	/** For 'account': the instance, its display name and driver. */
	provider: z.string().max(80).optional(),
	account: z.string().max(80).optional(),
	driver: z.string().max(80).optional(),
	/** The installed and the newest version, when known. */
	version: z.string().max(40).optional(),
	latestVersion: z.string().max(40).optional(),
	/**
	 * Why it isn't ok, as a code the app words: 'offline' | 'behind' | 'updateFailed' | 'toolDown' |
	 * 'signIn' | 'notInstalled' | 'unavailable' | 'disabled' | 'updateAvailable'.
	 */
	reason: z.string().max(40).optional(),
	/** The machine's or tool's own words for the problem. */
	detail: z.string().max(400).optional(),
	/** Since when (offline since, signed out at, failing since). */
	since: z.string().max(40).optional(),
	/** What fixes it from Konvo, if anything: sign in, update the agent, or a command to run by hand. */
	fix: z.enum(['signIn', 'updateAgent', 'command']).optional(),
	command: z.string().max(5000).optional(),
});
export type AgentHealthCheck = z.infer<typeof AgentHealthCheckSchema>;

export const AgentMachineHealthSchema = z.object({
	checkedAt: z.string().max(40),
	/** The worst check's state. */
	state: z.enum(AGENT_HEALTH_STATES),
	checks: z.array(AgentHealthCheckSchema).max(50),
	/** warn + error events in the last 7 days. */
	problemsThisWeek: z.number().int().nonnegative(),
});
export type AgentMachineHealth = z.infer<typeof AgentMachineHealthSchema>;

/** GET /konvo/agents/machines/:id/activity (owner) and GET /konvo/agents/team/machines/:id/activity (staff). */
export const AgentActivitySchema = z.object({
	events: z.array(AgentActivityEventSchema).max(AGENT_ACTIVITY_MAX_EVENTS),
	health: AgentMachineHealthSchema,
});
export type AgentActivity = z.infer<typeof AgentActivitySchema>;

/** POST /konvo/agents/bridge/results */
export const AgentResultsSchema = z.object({
	results: z
		.array(
			z.union([
				z.object({id: z.string(), ok: z.literal(true), data: z.unknown()}),
				z.object({id: z.string(), ok: z.literal(false), error: AgentErrorSchema}),
			]),
		)
		.max(50),
});

export const AgentEventSchema = z.discriminatedUnion('type', [
	z.object({type: z.literal('machine'), machine: AgentMachineInfoSchema}),
	z.object({type: z.literal('threads'), threads: z.array(AgentThreadSummarySchema).max(2000)}),
	z.object({type: z.literal('thread'), thread: AgentThreadDetailSchema}),
	z.object({type: z.literal('activity'), activity: z.array(AgentBridgeActivitySchema).max(20)}),
	AgentLegacyTerminalEventSchema,
]);
export type AgentEvent = z.infer<typeof AgentEventSchema>;

/** POST /konvo/agents/bridge/events */
export const AgentEventsSchema = z.object({events: z.array(AgentEventSchema).max(20)});

/**
 * The bytes of an attachment or a reply's picture or video: base64 in the result, or (a big file, when
 * the command asked for `link`) uploaded first to POST /konvo/agents/bridge/downloads and named here.
 */
export const AgentFileDataSchema = z.union([
	z.object({
		name: z.string().max(300),
		mimeType: z.string().max(200),
		size: z.number().int().nonnegative().max(AGENT_ATTACHMENT_MAX_BYTES),
		/** Base64. */
		data: z.string().max(Math.ceil(AGENT_ATTACHMENT_MAX_BYTES / 3) * 4 + 4),
	}),
	z.object({
		name: z.string().max(300),
		mimeType: z.string().max(200),
		size: z.number().int().nonnegative().max(AGENT_ATTACHMENT_LINK_MAX_BYTES),
		downloadId: z.string().regex(AGENT_DOWNLOAD_ID_PATTERN),
	}),
]);
export type AgentFileData = z.infer<typeof AgentFileDataSchema>;

/** Data each op returns in a successful result. */
export const AgentOpDataSchemas = {
	commands: AgentCommandsSchema,
	resetUsage: AgentUsageResetResultSchema,
	agentSetup: AgentToolInfoSchema,
	agentUpdate: AgentToolInfoSchema,
	account: AgentToolInfoSchema,
	deviceAccess: AgentToolInfoSchema,
	threads: z.object({threads: z.array(AgentThreadSummarySchema), projects: z.array(AgentProjectSchema)}),
	thread: AgentThreadDetailSchema,
	watch: z.object({watching: z.array(z.string())}),
	send: z.object({accepted: z.literal(true)}),
	start: z.object({threadId: z.string()}),
	interrupt: z.object({accepted: z.literal(true)}),
	compact: z.object({accepted: z.literal(true)}),
	restartSession: z.object({accepted: z.literal(true)}),
	/** The prompt it went back past (for the composer); `done` once the agent tool confirmed it. */
	rewind: z.object({accepted: z.literal(true), prompt: z.string().max(200_000), done: z.boolean()}),
	settle: z.object({accepted: z.literal(true)}),
	snooze: z.object({accepted: z.literal(true)}),
	queued: z.object({accepted: z.literal(true)}),
	stopDevServer: z.object({accepted: z.literal(true)}),
	rename: z.object({accepted: z.literal(true)}),
	archive: z.object({accepted: z.literal(true)}),
	delete: z.object({accepted: z.literal(true)}),
	archived: z.object({threads: z.array(AgentThreadSummarySchema).max(500)}),
	respond: z.object({accepted: z.literal(true)}),
	attachment: AgentFileDataSchema,
	answer: z.object({accepted: z.literal(true)}),
	/** `stopped`: the turn waited on the question and was stopped. */
	dismiss: z.object({accepted: z.literal(true), stopped: z.literal(true).optional()}),
	secret: z.object({accepted: z.literal(true)}),
	branches: AgentBranchesSchema,
	push: z.object({pushed: z.literal(true)}),
	cloudHandoff: AgentCloudHandoffSchema,
	toProject: AgentToProjectResultSchema,
	media: AgentFileDataSchema,
	document: AgentDocumentSchema,
	documentAsset: z.object({
		name: z.string().max(300),
		mimeType: z.string().max(200),
		size: z.number().int().nonnegative().max(AGENT_ATTACHMENT_MAX_BYTES),
		/** Base64. */
		data: z.string().max(Math.ceil(AGENT_ATTACHMENT_MAX_BYTES / 3) * 4 + 4),
	}),
	subagent: AgentSubagentSessionSchema,
	subagentPicture: z.object({
		name: z.string().max(300),
		mimeType: z.string().max(200),
		size: z.number().int().nonnegative().max(AGENT_ATTACHMENT_MAX_BYTES),
		/** Base64. */
		data: z.string().max(Math.ceil(AGENT_ATTACHMENT_MAX_BYTES / 3) * 4 + 4),
	}),
	folders: AgentFoldersSchema,
	repos: AgentReposSchema,
	addProject: AgentAddedProjectSchema,
	renameProject: z.object({accepted: z.literal(true)}),
	removeProject: z.object({accepted: z.literal(true), chats: z.number().int().nonnegative()}),
	diff: AgentDiffSchema,
	search: AgentSearchSchema,
	fileRefs: AgentFileRefsSchema,
	fileList: AgentFileListSchema,
	fileSearch: AgentFileSearchSchema,
	fileText: AgentFileTextSchema,
	file: AgentFileDataSchema,
	browserTunnel: AgentBrowserTunnelDataSchema,
	devServers: AgentDevServersSchema,
	machineStats: AgentMachineStatsSchema,
	terminalTunnel: AgentTerminalTunnelDataSchema,
} as const satisfies Record<AgentOp, z.ZodType>;

// ---------------------------------------------------------------------------
// What the app sees
// ---------------------------------------------------------------------------

export const AgentMachineViewSchema = z.object({
	id: z.string(),
	name: z.string(),
	platform: AgentMachineInfoSchema.shape.platform,
	online: z.boolean(),
	lastSeenAt: z.string().nullable(),
	connectedAt: z.string(),
	bridgeVersion: z.string(),
	tools: z.array(AgentToolInfoSchema),
	/** Why the tool can't be reached while `tools` is empty; null when fine or the bridge doesn't say. */
	toolProblem: AgentToolProblemSchema.nullable().optional(),
	limits: AgentLimitsSchema,
	projects: z.array(AgentProjectSchema),
	/** Running / needs-you counts from the latest thread list while online. */
	counts: z.object({running: z.number().int(), needsYou: z.number().int()}),
	/** Health checks and the week's problem count (Settings -> Connections -> Agents). */
	health: AgentMachineHealthSchema.optional(),
	/** The three newest activity events, for the machine card. */
	recentActivity: z.array(AgentActivityEventSchema).max(3).optional(),
});
export type AgentMachineView = z.infer<typeof AgentMachineViewSchema>;

/** One machine in Team insights -> Agent machines (staff only). */
export const AgentTeamMachineSchema = z.object({
	machineId: z.string(),
	userId: z.string(),
	/** The owner's display name and avatar. */
	name: z.string().max(200),
	avatar: z.string().nullable().optional(),
	machineName: z.string().max(80),
	platform: AgentMachineInfoSchema.shape.platform,
	online: z.boolean(),
	lastSeenAt: z.string().nullable(),
	bridgeVersion: z.string().max(40),
	latestBridgeVersion: z.string().max(40).optional(),
	tool: z.object({name: z.string().max(80), version: z.string().max(40)}).nullable(),
	health: AgentMachineHealthSchema,
	/** The newest warn / error event in the last 28 days. */
	lastProblem: AgentActivityEventSchema.nullable(),
	problems: z.object({week: z.number().int().nonnegative(), fourWeeks: z.number().int().nonnegative()}),
});
export type AgentTeamMachine = z.infer<typeof AgentTeamMachineSchema>;

/** GET /konvo/agents/team/machines (staff only; 403 for everyone else). */
export const AgentTeamMachinesSchema = z.object({machines: z.array(AgentTeamMachineSchema).max(500)});
export type AgentTeamMachines = z.infer<typeof AgentTeamMachinesSchema>;

/** GET /konvo/agents */
export const AgentsOverviewSchema = z.object({
	enabled: z.boolean(),
	machines: z.array(AgentMachineViewSchema),
});
export type AgentsOverview = z.infer<typeof AgentsOverviewSchema>;

/** POST /konvo/agents/pairing -> a one-time code for `konvo-agents connect`. */
export const AgentPairingSchema = z.object({
	code: z.string(),
	expiresAt: z.string(),
	command: z.string(),
	/** True once this person has connected a machine before (the dialog folds the setup step). */
	setupDone: z.boolean(),
});
export type AgentPairing = z.infer<typeof AgentPairingSchema>;

/** Notification settings: Settings -> Notifications -> Agents. */
export const AgentsPrefsSchema = z.object({
	/** The main agent is done: its turn ended and no subagents or background jobs are left running. */
	finished: z.boolean(),
	/** A subagent or background job came back while others still run. Left out: off. */
	subagentFinished: z.boolean().optional(),
	failed: z.boolean(),
	needsYou: z.boolean(),
	/** 'all' threads, or only threads started or prompted from Konvo. */
	scope: z.enum(['all', 'konvo']),
	/**
	 * No longer a choice: agent notices are always pushes from Agents, and the Assistant DM's Agents
	 * card updates without notifying. The API always answers 'push'; kept for older apps.
	 */
	channel: z.enum(['dm', 'push']),
	/** Tell me when a plan window reaches 90% (once per window). Left out: on. */
	usageAlert: z.boolean().optional(),
	/** Tell me when a used-up plan limit resets. Left out: on. */
	usageReset: z.boolean().optional(),
	/**
	 * Phones: one Live Activity (iOS) or ongoing notification (Android) for every running agent,
	 * with Allow / Deny for the first approval. Left out: on.
	 */
	liveActivity: z.boolean().optional(),
	/**
	 * What Enter does with a message sent while the agent works: 'steer' into the running turn, or
	 * 'queue' it to go once the turn ends. ⌘/Ctrl+Enter does the other. Left out: steer.
	 */
	followUp: z.enum(['steer', 'queue']).optional(),
	/**
	 * Pushes about pull requests (design/pr-tool): asked to review, checks failed or ready to merge
	 * on yours, reviews, merges by someone else and new comments. Left out: on.
	 */
	pullUpdates: z.boolean().optional(),
});
export type AgentsPrefs = z.infer<typeof AgentsPrefsSchema>;
export const DEFAULT_AGENTS_PREFS: AgentsPrefs = {
	finished: true,
	subagentFinished: false,
	failed: true,
	needsYou: true,
	scope: 'all',
	channel: 'push',
	usageAlert: true,
	usageReset: true,
	liveActivity: true,
	followUp: 'steer',
	pullUpdates: true,
};

/**
 * A prompt Konvo sends to a thread later, e.g. "continue" once the plan's limit resets. One per
 * thread; it goes when `at` comes if the machine is online (otherwise Konvo says it could not).
 */
const AgentHeldPayload = {
	attachmentNames: z.array(z.string().max(255)).max(AGENT_UPLOAD_MAX_FILES).optional(),
	sendId: z.string().uuid().optional(),
	attachments: z.array(AgentUploadInputSchema).max(AGENT_UPLOAD_MAX_FILES).optional(),
	mentions: z.array(AgentMentionSchema).max(AGENT_MENTIONS_MAX).optional(),
	model: AgentModelChoiceSchema.optional(),
};
export const AgentQueuedSendSchema = z.object({
	...AgentHeldPayload,
	machineId: z.string().max(20),
	threadId: z.string().max(200),
	prompt: z.string().max(20_000),
	at: z.string().max(40),
	reason: z.enum(['limit']).optional(),
	state: z.enum(['waiting', 'sending', 'failed']).optional(),
	error: z.string().max(400).optional(),
});
export type AgentQueuedSend = z.infer<typeof AgentQueuedSendSchema>;

/** POST /konvo/agents/machines/:id/threads/:threadId/queued */
export const AgentQueueSendRequestSchema = z
	.object({
		...AgentHeldPayload,
		prompt: z.string().trim().max(20_000),
		at: z.string().max(40),
	})
	.refine((body) => body.prompt.length > 0 || (body.attachments?.length ?? 0) > 0, {
		message: 'Write a message or add a picture or file.',
	});

/** GET /konvo/agents/queued */
export const AgentsQueuedSchema = z.object({queued: z.array(AgentQueuedSendSchema)});

/** GET /konvo/insights/agent-plans (staff): everyone's agent plans and how often they ran out. */
export const AgentTeamPlansSchema = z.object({
	people: z.array(
		z.object({
			user_id: z.string(),
			name: z.string(),
			/** Avatar hash, as Team insights' other person rows carry it. */
			avatar: z.string().nullable(),
			plans: z.array(
				z.object({
					provider: z.string(),
					providerName: z.string(),
					plan: z.string().optional(),
					checkedAt: z.string(),
					windows: z.array(AgentUsageWindowSchema.extend({reset: z.boolean().optional()})),
					machines: z.array(z.string()),
				}),
			),
			/** Plan windows used up in the last 7 days and the last 28 days. */
			ranOut: z.object({thisWeek: z.number().int(), lastFourWeeks: z.number().int()}),
		}),
	),
});
export type AgentTeamPlans = z.infer<typeof AgentTeamPlansSchema>;

/**
 * Which finished threads the person has opened, kept per account so phone and desktop agree. A
 * thread that finished after `since` and after its own mark shows the green "not opened" tick.
 */
export const AgentsSeenSchema = z.object({
	/** When the person first had seen marks; anything finished before counts as seen. */
	since: z.string().max(40),
	/** `<machineId>/<threadId>` -> the finish time the person has seen. */
	threads: z.record(z.string().max(420), z.string().max(40)),
	/** `<machineId>/<threadId>` the person marked unread: not opened again until they open it. */
	unread: z.array(z.string().max(420)).max(600).optional(),
});
export type AgentsSeen = z.infer<typeof AgentsSeenSchema>;

/** POST /konvo/agents/seen */
export const AgentSeenMarkSchema = z.object({
	machineId: z.string().max(20),
	threadId: z.string().max(200),
	/** The finish time that was on screen. */
	at: z.string().max(40),
	/** Mark it unread instead: it shows as not opened until it is opened again. */
	unread: z.boolean().optional(),
});
export type AgentSeenMark = z.infer<typeof AgentSeenMarkSchema>;

/** POST /konvo/agents/opened: a thread is on screen, so its push leaves the person's other devices. */
export const AgentOpenedSchema = z.object({
	machineId: z.string().max(20),
	threadId: z.string().max(200),
});
export type AgentOpened = z.infer<typeof AgentOpenedSchema>;

/**
 * Threads the person pinned, kept per account so phone and desktop agree: `<machineId>/<threadId>`,
 * the latest pin first. A pinned thread heads its group in the list and stays there, even once the
 * agent tool settles it.
 */
export const AgentsPinsSchema = z.object({
	threads: z.array(z.string().max(420)).max(200),
});
export type AgentsPins = z.infer<typeof AgentsPinsSchema>;

/** POST /konvo/agents/pins */
export const AgentPinMarkSchema = z.object({
	machineId: z.string().max(20),
	threadId: z.string().max(200),
	pinned: z.boolean(),
});
export type AgentPinMark = z.infer<typeof AgentPinMarkSchema>;

/**
 * A snoozed chat, kept per account like pins. It sleeps in the "Snoozed" group of its project until
 * `until`, or with untilNeedsYou until it finishes, fails or asks something. It stays listed for a
 * while after it ends so the chat can say it's back from snooze, until the person opens it.
 */
export const AgentSnoozeSchema = z.object({
	/** `<machineId>/<threadId>` */
	thread: z.string().max(420),
	until: z.string().max(40).optional(),
	untilNeedsYou: z.literal(true).optional(),
	/** When it was snoozed. */
	at: z.string().max(40),
});
export type AgentSnooze = z.infer<typeof AgentSnoozeSchema>;

export const AgentsSnoozesSchema = z.object({snoozes: z.array(AgentSnoozeSchema).max(200)});
export type AgentsSnoozes = z.infer<typeof AgentsSnoozesSchema>;

/** POST /konvo/agents/snoozes: snooze until a time, until it needs you, or wake it (both left out). */
export const AgentSnoozeMarkSchema = z.object({
	machineId: z.string().max(20),
	threadId: z.string().max(200),
	until: z.string().max(40).nullable().optional(),
	untilNeedsYou: z.literal(true).optional(),
});
export type AgentSnoozeMark = z.infer<typeof AgentSnoozeMarkSchema>;

/** The glyphs a project's icon can be (Tabler icon names), when it is not a picture. */
export const AGENT_PROJECT_GLYPHS = [
	'code',
	'rocket',
	'briefcase',
	'building-store',
	'building-factory-2',
	'truck',
	'shopping-cart',
	'chart-bar',
	'message-circle',
	'world',
	'device-mobile',
	'server',
	'database',
	'robot',
	'camera',
	'book',
	'palette',
	'leaf',
	'flask',
	'heart',
	'home',
	'school',
	'users',
	'lock',
	// The plain folder, picked on purpose over the project's own favicon.
	'folder',
] as const;

/** Largest picture icon, as a data URL (Konvo shrinks pictures to 64 px before saving). */
export const AGENT_PROJECT_ICON_IMAGE_MAX = 16_000;

/**
 * A project's icon in the Agents list, set by the person (account-wide, like pins): a glyph, or a
 * small picture such as the client's logo.
 */
export const AgentProjectIconSchema = z.union([
	z.object({glyph: z.enum(AGENT_PROJECT_GLYPHS)}),
	z.object({
		image: z
			.string()
			.max(AGENT_PROJECT_ICON_IMAGE_MAX)
			.regex(/^data:image\/(png|webp|jpeg);base64,[A-Za-z0-9+/]+=*$/),
	}),
]);
export type AgentProjectIcon = z.infer<typeof AgentProjectIconSchema>;

/** GET /konvo/agents/project-icons: `<machineId>/<projectId>` -> its icon. */
export const AgentsProjectIconsSchema = z.object({
	icons: z.record(z.string().max(420), AgentProjectIconSchema),
});
export type AgentsProjectIcons = z.infer<typeof AgentsProjectIconsSchema>;

/** POST /konvo/agents/project-icons: set a project's icon, or clear it with null. */
export const AgentProjectIconMarkSchema = z.object({
	machineId: z.string().max(20),
	projectId: z.string().max(200),
	icon: AgentProjectIconSchema.nullable(),
});
export type AgentProjectIconMark = z.infer<typeof AgentProjectIconMarkSchema>;

/** Gateway dispatch KONVO_AGENTS_UPDATE, sent to the machine's owner. */
export const AgentsUpdateEventSchema = z.discriminatedUnion('kind', [
	z.object({kind: z.literal('machines')}),
	z.object({kind: z.literal('threads'), machineId: z.string(), threads: z.array(AgentThreadSummarySchema)}),
	z.object({kind: z.literal('thread'), machineId: z.string(), thread: AgentThreadDetailSchema}),
	/** The person opened a finished thread on another device (older apps ignore this kind). */
	z.object({
		kind: z.literal('seen'),
		machineId: z.string(),
		threadId: z.string(),
		at: z.string(),
		/** Marked unread rather than opened. */
		unread: z.boolean().optional(),
	}),
	/** The person pinned or unpinned a thread on another device (older apps ignore this kind). */
	z.object({kind: z.literal('pins'), threads: z.array(z.string())}),
	/** The person snoozed or woke a chat on another device (older apps ignore this kind). */
	z.object({kind: z.literal('snoozes'), snoozes: z.array(AgentSnoozeSchema)}),
	/** The person set or cleared a project's icon on another device (older apps ignore this kind). */
	z.object({kind: z.literal('project_icons'), icons: z.record(z.string(), AgentProjectIconSchema)}),
	/** The person's queued sends changed (older apps ignore this kind). */
	z.object({kind: z.literal('queued')}),
	/** Someone asked you to merge, or one of your asks was handled (older apps ignore this kind). */
	z.object({kind: z.literal('merge_asks')}),
	/** Pull requests in the person's list changed on GitHub (pullKey of each; older apps ignore this kind). */
	z.object({kind: z.literal('pulls'), changed: z.array(z.string().max(300)).max(100)}),
	/** GitHub Actions runs in the person's repositories changed (older apps ignore this kind). */
	z.object({kind: z.literal('runs')}),
	/** Someone was asked one of a thread's questions, or answered it (older apps ignore this kind). */
	z.object({kind: z.literal('asks'), machineId: z.string(), threadId: z.string()}),
	/** Team sharing: a shared thread's people, presence, comments or waiting prompts changed (AgentShares.ts). */
	z.object({kind: z.literal('share'), machineId: z.string(), threadId: z.string()}),
	/** Team sharing: what is shared with you (or its machines' online state) changed. */
	z.object({kind: z.literal('shared')}),
]);
export type AgentsUpdateEvent = z.infer<typeof AgentsUpdateEventSchema>;

/** Error codes the API returns for agent operations (HTTP 409 unless noted). */
export const AGENT_ERROR_CODES = {
	offline: 'MACHINE_OFFLINE',
	readOnly: 'MACHINE_READ_ONLY',
	approvalsOff: 'APPROVALS_OFF',
	/** An answer from a notification or the lock screen: the request was answered or replaced since. */
	approvalGone: 'APPROVAL_GONE',
	/** An answer from a notification or the lock screen: the request now asks something else. */
	approvalChanged: 'APPROVAL_CHANGED',
	projectHidden: 'PROJECT_NOT_SHARED',
	timeout: 'MACHINE_TIMEOUT',
	notFound: 'NOT_FOUND', // 404
	notEnabled: 'AGENTS_NOT_ENABLED', // 403
	/** The machine's konvo-agents is too old for what was asked (a model choice, files on a new thread). */
	bridgeTooOld: 'BRIDGE_TOO_OLD',
	/** Pull requests: GitHub isn't connected, the person may not merge here, or GitHub refused. */
	githubNotConnected: 'GITHUB_NOT_CONNECTED',
	mergeNotAllowed: 'MERGE_NOT_ALLOWED', // 403
	mergeRefused: 'MERGE_REFUSED',
	/** A review, comment or other pull request action GitHub refused (design/agents-t3-parity). */
	pullRefused: 'PULL_REFUSED',
	/** The request is missing something it needs (the words of a reply, the thread). */
	badRequest: 'BAD_REQUEST', // 400
	noMergers: 'NO_MERGERS',
	/** Cloning: the machine itself is not signed in to GitHub (`gh`). Matches the bridge's own code. */
	noGithub: 'NO_GITHUB',
	/** Ask a colleague: the question is no longer open (answered, or the agent moved on). */
	askClosed: 'ASK_CLOSED',
	/** An answer from a widget: that question is no longer waiting (answered elsewhere, or the agent moved on). */
	questionGone: 'QUESTION_GONE',
	/** Ask a colleague: the answer is for other questions than the ones asked. */
	questionChanged: 'QUESTION_CHANGED',
	/** Ask a colleague: you can't ask that person (yourself, a bot, or no DM allowed). */
	askNotAllowed: 'ASK_NOT_ALLOWED',
	/** Files: over AGENT_FILE_MAX_BYTES (or AGENT_FILE_TEXT_MAX_BYTES as text). Matches the bridge's code. */
	fileTooLarge: 'FILE_TOO_LARGE',
	/** Files: on the secrets deny list; Konvo never opens it. Matches the bridge's code. */
	fileSecret: 'FILE_SECRET',
	/** Files: not text (asked for as text). Matches the bridge's code. */
	fileNotText: 'FILE_NOT_TEXT',
	/** Files: git ignores this folder; it stays closed. Matches the bridge's code. */
	fileIgnored: 'FILE_IGNORED',
	/** A new chat: the checkout couldn't switch to its branch (with switchProblem from 0.41.0). Matches the bridge's code. */
	switchFailed: 'SWITCH_FAILED',
	/** A new chat asked to stash: an agent is working in that checkout. Matches the bridge's code. */
	checkoutBusy: 'CHECKOUT_BUSY',
	/** A new chat asked to stash and git refused. Matches the bridge's code. */
	stashFailed: 'STASH_FAILED',
	/** A cloud chat: GitHub has no such branch to start from. Matches the bridge's code. */
	cloudNoBranch: 'CLOUD_NO_BRANCH',
	/** A cloud chat couldn't be set up on the machine (no git project, no Claude, T3 couldn't start it). Matches the bridge's code. */
	cloudUnavailable: 'CLOUD_UNAVAILABLE',
	/** Push: GitHub didn't take the branch (behind, or no rights). Matches the bridge's code. */
	pushFailed: 'PUSH_FAILED',
	/** Continue in the cloud: the work in progress could not be gathered or pushed. Matches the bridge's code. */
	handoffFailed: 'HANDOFF_FAILED',
} as const;
